<template>
  <div class="p-2">
    <div class="container-fluid pb-4">
      <!--Estimate Header-->
      <div v-if="!hasBundle">
        <div class="row header m-4">
          <div class="row">
            <h1>Patient Estimate</h1>
              <b-button class="ml-3" variant="outline-orange" @click.prevent="updateNotes">
                <font-awesome-icon class="orange mr-2" size="lg" :icon="['fal','note' ]"/>Notes
                <b-badge v-show="estimateNotes.length > 0" variant="orange">{{ estimateNotes.length }}</b-badge>
              </b-button>
          </div>
          <div class="col">
            <b-alert ref="alertContainer" :show="alertCountDown" dismissible fade :variant="alertVariant" @dismissed="alertCountDown=0">
              {{ alertMessage }}
            </b-alert>
            <b-alert v-if="generalErrorMessage" :show="true" fade variant="danger">
              {{ generalErrorMessage }}
            </b-alert>
          </div>
        </div>
        <!--Header Labels-->
      <div class="row patient-summary-header pt-2">
        <div class="col-6">
          <h5><a @click.prevent.stop="editPatient(patient.organizationPatientId)"
                 href="#">{{ patient.firstName + ' ' + patient.lastName }}&nbsp;&nbsp;<font-awesome-icon size="sm" :icon="['far', 'edit']"/></a>
          </h5>
        </div>
        <div class="col-6">
          <span v-if="!estimate.finalized && estimate.organizationBundleId === null && estimate.isArchived === false">
            <h5><a @click.prevent.stop="editEstimateType()" href="#">{{ estimateType }} <font-awesome-icon size="sm" :icon="['far', 'edit']"/></a></h5>
          </span>
          <span v-else>
            <h5>{{ estimateType }}</h5>
          </span>
        </div>
      </div>
        <!--Patient Summary-->
      <div class="row patient-summary py-2">
        <div class="col-2">
          <img class="headshot" :src="patient.imageUrl"/>
        </div>
        <div class="col-4">
          <span class="field-label">Birth Date: </span>{{ formatBirthDate(patient.dob) }}<br>
          <span class="field-label">MRN: </span>{{ patient.mrn }}<br>
          <span class="field-label">Patient ID: </span>{{ patient.patientIdFromOrg }}<br>
          <span class="field-label">Phone: </span>{{ patient.phoneNumber }}<br>
          <span class="field-label">E-Mail: </span>{{ patient.email }}<br>
          <span v-if="!estimate.finalized && estimate.isArchived === false">
            <span class="field-label">Date of Service:</span><br>
            <b-button id="popover-target-1" class="dateOfServiceField mt-2" :disabled="estimate.finalized || estimate.isArchived === true">{{ dateOfServiceCopy }}</b-button>
              <b-popover ref="popover" custom-class="wide-popover" target="popover-target-1" triggers="click blur" placement="bottomright">
                <b-calendar v-model="estimate.procedureDate" @selected="estimateChanged = true">
                <div>
                  <button
                    type="button"
                    class="btn btn-outline-orange float-right"
                  @click.prevent="closePopover()">
                    Close
                  </button>
                  <button
                      type="button"
                      class="btn btn-orange float-right mr-2"
                      @click.prevent="updateProcedureDate()">
                  Save
                  </button>
              </div>
            </b-calendar>
                 </b-popover>
          </span>
          <span v-else>
             <span class="field-label">Date of Service: </span> {{ formatDateUtc(estimate.procedureDate) }}
          </span>
          <!--ICD 10 Codes-->
            <div class="pt-4">
              <b-form-group
                  id="primaryIcdCmCodeGroup"
                  invalid-feedback="Primary ICD10 code is not valid"
                  label="Primary ICD10 Code"
                  label-class="field-label"
                  :disabled="estimate.finalized || estimate.isArchived === true">
                <autocomplete ref="primaryIcdAutocomplete" :options="matchedIcdCodes" v-model="primaryIcd" @select="selectPrimaryIcdCode" placeholder="Add code...">
                  <template slot-scope="{ option }">
                    <div class="content">
                      <p>{{ formatICD10(option.code) }}<br>
                        {{ option.description }}</p>
                    </div>
                  </template>
                </autocomplete>
              </b-form-group>
            </div>
            <b-form-group
                id="secondaryIcdCmCodeGroup"
                invalid-feedback="Secondary ICD10 code is not valid"
                label="Secondary ICD10 Code"
                label-class="field-label"
                :disabled="estimate.finalized || estimate.isArchived === true">
              <autocomplete :options="matchedIcdCodes" v-model="secondaryIcd" @select="selectSecondaryIcdCode" placeholder="Add code...">
                <template slot-scope="{ option }">
                  <div class="content">
                    <p>{{ formatICD10(option.code) }}<br>
                      {{ option.description }}</p>
                  </div>
                </template>
              </autocomplete>
            </b-form-group>
          </div>
          <!--Insurance-->
          <div class="col-6">
            <div v-if="!hasBundle && estimate.isSelfPay === false && estimate.insuranceVerification && estimate.insuranceVerification.insuranceEligibilityStatus === 'Verified'" class="row">
              <div class="col">
                <h5>Individual Coverage</h5>
                  <div>Deductible - {{ individualDeductibleDisplay }}</div>
                    <div v-if="hasIndividualDeductible" class="progress progress-orange">
                      <div class="progress-bar" role="progressbar"
                           :style="'width: ' + individualDeductiblePercent + '%'"></div>
                    </div>
                <div>Out of Pocket - {{ individualMaxOopDisplay }}</div>
                  <div v-if="hasIndividualMaxOop" class="progress progress-orange">
                      <div class="progress-bar" role="progressbar"
                           :style="'width: ' + individualOutOfPocketPercent + '%'"></div>
                  </div>
                <div>Professional coinsurance: {{ coinsuranceTotal }}<br/>
                  <div v-if="estimate.isAsc"> Facility coinsurance: {{ ascCoinsuranceTotal }}<br/></div>
                  Insurance <span v-if="estimate.benefitOverride">manually </span>verified on
                  <span>{{ formatBirthDate(estimate.insuranceVerification.autoVerificationDate) }}</span>
                </div>
              </div>
              <div class="col">
                <div class="row">
                  <div class="col-12">
                    <template v-if="hasFamilyBenefits">
                      <h5>Family Coverage</h5>
                      <div>Deductible - {{ familyDeductibleDisplay }}</div>
                      <div v-if="hasFamilyDeductible" class="progress progress-orange">
                        <div class="progress-bar" role="progressbar"
                             :style="'width: ' + familyDeductiblePercent + '%'"></div>
                      </div>
                      <div>Out of Pocket - {{ familyMaxOopDisplay }}</div>
                      <div v-if="hasFamilyMaxOop" class="progress progress-orange">
                        <div class="progress-bar" role="progressbar"
                             :style="'width: ' + familyOutOfPocketPercent + '%'"></div>
                      </div>
                    </template>
                  </div>
                  <div class="col-12" v-if="estimate.benefitOverride && estimateChanged === false && !estimate.finalized && estimate.isArchived === false">
                    <button type="button" @click.prevent="showingEditBenefitsModal = true"
                            class="m-2 btn btn-outline-orange float-left benefits-btn">Edit benefits
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="estimate.isSelfPay === false && estimate.insuranceVerification && estimate.insuranceVerification.insuranceEligibilityStatus !== 'Verified'" class="row">
              <div class="col">
                <b-alert :show="true" variant="danger">
                  Insurance Verification Error: {{ estimate.insuranceVerification.insuranceEligibilityStatus }}
                  <template v-if="estimate.insuranceVerification.verificationError">
                    <br/>{{ estimate.insuranceVerification.verificationError }}
                  </template>
                </b-alert>
              </div>
            </div>
            <div v-if="!hasBundle && estimate.isSelfPay === false" class="row">
              <div class="col">
                <b-form-checkbox v-model="estimate.benefitOverride" :disabled="estimate.finalized || estimate.isArchived === true">
                  Override benefits
                </b-form-checkbox>
              </div>
            </div>
            <div v-if="estimate.insuranceBenefitInformationPresent === true">
              <span class="field-label">Eligibility: </span> <a @click.prevent="viewBenefits(estimate.insuranceVerification.organizationPatientInsuranceVerificationId)" href="#">View Eligibility Details</a><br>
            </div>
            <div v-if="estimate.ascInsuranceBenefitInformationPresent === true">
              <span class="field-label">ASC Eligibility: </span><a @click.prevent="viewBenefits(estimate.ascInsuranceVerification.organizationPatientInsuranceVerificationId)" href="#">View ASC Eligibility Details</a><br>
            </div>
            <div v-if="!hasBundle && patient.hasEmployerBenefits === true">
              <font-awesome-icon class="orange mr-1" size="sm" :icon="['far','chart-network']"/><strong>Employer Benefits Available</strong><br>
            </div>
          </div>
        </div>
      </div>
      <!--Patient Episode Header-->
      <div v-show="hasBundle">
        <div class="col mt-3">
          <b-alert ref="alertContainer" :show="alertCountDown" dismissible fade :variant="alertVariant" @dismissed="alertCountDown=0">
            {{ alertMessage }}
          </b-alert>
          <b-alert v-if="generalErrorMessage" :show="true" fade variant="danger">
            {{ generalErrorMessage }}
          </b-alert>
        </div>
        <div class="row episode-header m-4">
          <div class="row">
      <div><h1>Patient Episode </h1></div>
            <div><b-button class="ml-3" variant="outline-orange" @click.prevent="updateNotes">
              <font-awesome-icon class="orange mr-2" size="lg" :icon="['fal','note' ]"/>Notes
              <b-badge v-show="estimateNotes.length > 0" variant="orange">{{ estimateNotes.length }}</b-badge>
            </b-button>
            </div>
            <div>
          </div>
          </div>
          <div v-if="estimate.employer && estimate.employer.logoUrl" class="text-right">
            <img class="employer-logo" :src="estimate.employer.logoUrl">
          </div>
        </div>
        <!--Header Labels-->
      <div class="row patient-summary-header pt-2">
        <div class="col-sm-4">
          <h5><a @click.prevent.stop="editPatient(patient.organizationPatientId)"
                 href="#">{{ patient.firstName + ' ' + patient.lastName }}&nbsp;&nbsp;<font-awesome-icon size="sm" :icon="['far', 'edit']"/>
          </a></h5>
        </div>
          <div class="col-sm-4">
          <span v-if="!estimate.finalized && estimate.organizationBundleId === null && estimate.isArchived === false">
            <h5><a @click.prevent.stop="editEstimateType()" href="#">{{ estimateType }} <font-awesome-icon size="sm" :icon="['far', 'edit']"/></a></h5>
          </span>
              <span v-else>
            <h5>{{ estimateType }}</h5>
          </span>
          </div>
        <div v-if="estimate.bundle && estimate.bundle.patientAmount && estimate.insurance && estimate.insurance.insuranceCompany" class="col-4">
           <h5>{{ estimate.insurance.insuranceCompany }}</h5>
        </div>
      </div>
        <!--Patient Summary-->
      <div class="row patient-summary py-2">
        <div class="col-md-4">
          <img class="headshot my-2" :src="patient.imageUrl"/>
          <br>
          <span class="field-label">Birth Date: </span>{{ formatBirthDate(patient.dob) }}<br>
          <span class="field-label">MRN: </span>{{ patient.mrn }}<br>
          <span class="field-label">Patient ID: </span>{{ patient.patientIdFromOrg }}<br>
          <span class="field-label">Phone: </span>{{ patient.phoneNumber }}<br>
          <span class="field-label">E-Mail: </span>{{ patient.email }}<br>
          <span v-if="!estimate.finalized && estimate.isArchived === false">
          </span>
          <span v-else>
             <span class="field-label">Date of Service: </span> {{ formatDateUtc(estimate.procedureDate) }}
          </span>
          <!--ICD Codes-->
          <div v-show="hasBundle">
            <div class="icd-code-container pt-4">
              <b-form-group
                  id="primaryIcdCmCodeGroup"
                  invalid-feedback="Primary ICD10 code is not valid"
                  label="Primary ICD10 Code"
                  label-class="field-label"
                  :disabled="estimate.finalized || estimate.isArchived === true">
                <autocomplete ref="primaryIcdAutocomplete" :options="matchedIcdCodes" v-model="primaryIcd" @select="selectPrimaryIcdCode" placeholder="Add code...">
                  <template slot-scope="{ option }">
                    <div class="content">
                      <p>{{ formatICD10(option.code) }}<br>
                        {{ option.description }}</p>
                    </div>
                  </template>
                </autocomplete>
              </b-form-group>
              <b-form-group
                  id="secondaryIcdCmCodeGroup"
                  invalid-feedback="Secondary ICD10 code is not valid"
                  label="Secondary ICD10 Code"
                  label-class="field-label"
                  :disabled="estimate.finalized || estimate.isArchived === true">
                <autocomplete :options="matchedIcdCodes" v-model="secondaryIcd" @select="selectSecondaryIcdCode" placeholder="Add code...">
                  <template slot-scope="{ option }">
                    <div class="content">
                      <p>{{ formatICD10(option.code) }}<br>
                        {{ option.description }}</p>
                    </div>
                  </template>
                </autocomplete>
              </b-form-group>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <template id="birthDateFieldGroup">
              <span class="field-label">Episode Start Date:</span>
              <b-row class="mt-1">
                <b-col md="auto">
                  <b-calendar
                      :disabled="estimate.isArchived === true"
                      width="350px"
                      invalid-feedback="Date of Service is not valid"
                      locale="en-US"
                      @selected="estimateChanged = true"
                      v-model="estimate.procedureDate">
                  </b-calendar>
                </b-col>
              </b-row>
          </template>
        </div>
        <!--Insurance-->
        <div v-if="estimate.bundle && estimate.bundle.patientPaymentType !== 'None'" class="col-md-4">
          <div v-if="estimate.isSelfPay === false && estimate.insuranceVerification && estimate.insuranceVerification.insuranceEligibilityStatus === 'Verified'" class="row">
            <div class="col">
              <h5>Individual Coverage</h5>
              <div>Deductible - {{ individualDeductibleDisplay }}</div>
              <div v-if="hasIndividualDeductible" class="progress progress-orange">
                <div class="progress-bar" role="progressbar"
                     :style="'width: ' + individualDeductiblePercent + '%'"></div>
              </div>
              <div>Out of Pocket - {{ individualMaxOopDisplay }}</div>
              <div v-if="hasIndividualMaxOop" class="progress progress-orange">
                <div class="progress-bar" role="progressbar"
                     :style="'width: ' + individualOutOfPocketPercent + '%'"></div>
              </div>
              <div>Professional coinsurance: {{ coinsuranceTotal }}<br/>
                <div v-if="estimate.isAsc"> Facility coinsurance: {{ ascCoinsuranceTotal }}<br/></div>
                Insurance <span v-if="estimate.benefitOverride">manually </span>verified on
                <span>{{ formatBirthDate(estimate.insuranceVerification.autoVerificationDate) }}</span>
              </div>
            </div>
            <div class="col">
              <div class="row">
                <div class="col-12">
                  <template v-if="hasFamilyBenefits">
                    <h5>Family Coverage</h5>
                    <div>Deductible - {{ familyDeductibleDisplay }}</div>
                    <div v-if="hasFamilyDeductible" class="progress progress-orange">
                      <div class="progress-bar" role="progressbar"
                           :style="'width: ' + familyDeductiblePercent + '%'"></div>
                    </div>
                    <div>Out of Pocket - {{ familyMaxOopDisplay }}</div>
                    <div v-if="hasFamilyMaxOop" class="progress progress-orange">
                      <div class="progress-bar" role="progressbar"
                           :style="'width: ' + familyOutOfPocketPercent + '%'"></div>
                    </div>
                  </template>
                </div>
                <div class="col-12" v-if="estimate.benefitOverride && estimateChanged === false && !estimate.finalized && estimate.isArchived === false">
                  <button type="button" @click.prevent="showingEditBenefitsModal = true"
                          class="m-2 btn btn-outline-orange float-left benefits-btn">Edit benefits
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="estimate.isSelfPay === false && estimate.insuranceVerification && estimate.insuranceVerification.insuranceEligibilityStatus !== 'Verified'" class="row">
            <div class="col">
              <b-alert :show="true" variant="danger">
                Insurance Verification Error: {{ estimate.insuranceVerification.insuranceEligibilityStatus }}
                <template v-if="estimate.insuranceVerification.verificationError">
                  <br/>{{ estimate.insuranceVerification.verificationError }}
                </template>
              </b-alert>
            </div>
          </div>
          <div v-if="estimate.isSelfPay === false" class="row">
            <div class="col">
              <b-form-checkbox v-model="estimate.benefitOverride" :disabled="estimate.finalized || estimate.isArchived === true">
                Override benefits
              </b-form-checkbox>
            </div>
          </div>
          <div v-if="estimate.insuranceBenefitInformationPresent === true">
            <span class="field-label">Eligibility: </span> <a @click.prevent="viewBenefits(estimate.insuranceVerification.organizationPatientInsuranceVerificationId)" href="#">View Eligibility Details</a><br>
          </div>
          <div v-if="estimate.ascInsuranceBenefitInformationPresent === true">
            <span class="field-label">ASC Eligibility: </span><a @click.prevent="viewBenefits(estimate.ascInsuranceVerification.organizationPatientInsuranceVerificationId)" href="#">View ASC Eligibility Details</a><br>
          </div>
          <div v-if="!hasBundle && patient.hasEmployerBenefits === true">
            <font-awesome-icon class="orange mr-1" size="sm" :icon="['far','chart-network']"/><strong>Employer Benefits Available</strong><br>
          </div>
        </div>
<!--        Info Header for Episode-->
        <div v-if="hasBundle" class="row episode-row pt-4 mx-2">
          <div class="col-12">
          </div>
          <div class="col-12">
            <div class="episode-detail-block">
              <div class="detail-header">
                <h5 class="mb-2" v-if="hasBundle">Anchor Provider Group:</h5>
                {{ estimate.organizationName }}<br>
                Tax ID: {{ estimate.taxId }}<br>
                <template v-if="estimate.isAsc">
                  Surgery Center: {{ estimate.ascOrganizationName }}<br>
                  Tax ID: {{ estimate.ascTaxId }}<br>
                </template>
                <br>
              </div>
              <div class="detail-header">
                <h5 class="mb-2"><template v-if="hasBundle">Anchor </template>Provider:</h5>
                {{ estimate.providerName }}<br>
                <template v-if="estimate.provider">
                  NPI: {{ estimate.provider.npi }}<br>
                </template>
                <br>
              </div>
              <div class="detail-header">
                <h5 class="mb-2">Location:</h5>
                <span v-if="!estimate.finalized && estimate.isArchived === false" >
          <a @click.prevent.stop="showingEstimateLocation = true" href="#">{{ estimate.location.name }}&nbsp;&nbsp;<font-awesome-icon
              size="sm" :icon="['far', 'edit']"/>
        </a><br>
        </span>
                <span v-if="estimate.finalized || estimate.isArchived === true">
          <div>{{ estimate.location.name }}</div><br>
        </span>
                Type: {{ estimate.location.type }}<br>
                {{ estimate.location.street1 }}<br>
                <template v-if="estimate.location.street2">
                  {{ estimate.location.street2 }}<br>
                </template>
                {{ estimate.location.city }}, {{ estimate.location.stateShort }} {{ estimate.location.zip }}<br><br>
              </div>
              <div class="detail-header">
                <h5 class="mb-2">Activity:</h5>
                <div v-if="estimate.createdByUser">Created: {{ createdUser }}<br></div>
                <div v-if="estimate.lastUpdatedByUser">Last Updated By: {{ lastUpdated }}<br></div>
                <div v-if="!estimate.lastUpdatedByUser">Last Updated: {{ lastUpdated }}<br></div>
                Email Last Sent: {{ emailLastSent }}<br>
                Sms Last Sent: {{ smsLastSent }}<br>
                <br>
              </div>
            </div>
          </div>
          <div class="col-2">
          </div>
        </div>
      </div>
      </div>
<!--      Estimate Portion-->
      <div v-if="hasBundle" class="row py-2">
        <div class="col">
          <h5>Episode</h5>
        </div>
        <div class="col text-right">
          <button v-if="estimate.isArchived === false" type="button" class="ml-2 btn btn-orange float-right" @click.prevent="confirmEstimateArchive(estimate)">Archive Episode</button>
          <button v-if="estimate.isArchived === true" type="button" class="ml-2 btn btn-orange float-right" @click.prevent="archiveEstimate(estimate.organizationPatientEstimateId)">Unarchive Episode</button>
          <button type="button" :disabled="estimateChanged == false && estimate.isArchived === true" class="btn btn-orange float-right" @click.prevent="updateEstimate()">
            <font-awesome-icon size="lg" class="mr-2" v-show="isSaving" icon="spinner" pulse/>
            Update Episode
          </button>
        </div>
      </div>
      <div v-if="hasBundle" class="row estimate-header py-2">
        <div class="col-auto line-action-header-col"></div>
        <div class="col-3"><h6>PROCEDURE</h6></div>
        <div class="col-6"><h6>DESCRIPTION</h6></div>
        <div class="col-2 text-right"><h6>AMOUNT</h6></div>
      </div>
      <div v-if="hasBundle" class="row bundle-container py-2">
        <div class="col-auto line-action-header-col"></div>
        <div class="col-3">{{ formatTerm(estimate.bundle.durationDays) + estimate.bundle.name }}</div>
        <div class="col-6">{{ estimate.bundle.patientDescription ? estimate.bundle.patientDescription :estimate.bundle.description }}</div>
        <div class="col-2 text-right">
          {{ formatNumber(estimate.bundle.amount) }}
        </div>
      </div>
      <div v-if="hasBundle && estimate.patientAmount > 0">
      <div class="row py-2">
        <div class="col">
          <h5>Patient Cost</h5>
        </div>
      </div>
      <div class="row estimate-header py-2">
        <div class="col-auto line-action-header-col"></div>
        <div class="col-6"><h6>DESCRIPTION</h6></div>
        <div class="col-3"></div>
        <div class="col-2 text-right"><h6>AMOUNT</h6></div>
      </div>
        <div class="row bundle-container py-2">
          <div class="col-auto line-action-header-col"></div>
          <div class="col-6">Patient Cost</div>
          <div class="col-3"></div>
          <div class="col-2 text-right">
            {{ formatNumber(estimate.patientAmount) }}
          </div>
        </div>
      </div>
      <div v-if="!hasBundle" class="row py-2">
        <div class="col">
          <h5>Professional Fees</h5>
        </div>
        <div class="col text-right">
          <button v-if="estimate.isArchived === false" type="button" class="ml-2 btn btn-orange float-right" @click.prevent="confirmEstimateArchive(estimate)">Archive Estimate</button>
          <button v-if="estimate.isArchived === true" type="button" class="ml-2 btn btn-orange float-right" @click.prevent="archiveEstimate(estimate.organizationPatientEstimateId)">Unarchive Estimate</button>
          <button :disabled="!isOrgAdmin && !isAdmin" type="button" class="ml-2 btn btn-orange float-right" @click.prevent="createEstimateTemplate(estimate)">Save as Template</button>
          <button :disabled="(!isOrgAdmin && !isAdmin && estimate.finalized) || estimate.isArchived === true" type="button" class="ml-2 btn btn-orange float-right" @click.prevent="finalizeEstimate">{{ estimate.finalized ? 'Reopen Estimate' : 'Finalize Estimate'}}</button>
            <button v-if="!hasBundle || (hasBundle && !estimate.bundle.hasPaymentDistributions)" type="button" :disabled="estimateChanged == false || estimate.finalized || estimate.isArchived === true" class="btn btn-orange float-right" @click.prevent="updateEstimate()">
            <font-awesome-icon size="lg" class="mr-2" v-show="isSaving" icon="spinner" pulse/>
            Update Estimate
          </button>
        </div>
      </div>
      <div v-if="!hasBundle" class="row estimate-header py-2">
        <div class="col-auto line-action-header-col"></div>
        <div class="col-4"><h6>CODE</h6></div>
        <div class="col-2"><h6>MODIFIER</h6></div>
        <div class="col-2 text-center"><h6>ALLOWABLE RATE</h6></div>
        <div class="col-1 text-center"><h6>QUANTITY</h6></div>
        <div class="col-2 text-right"><h6 v-if="estimate.isSelfPay">AMOUNT</h6><h6 v-if="!estimate.isSelfPay">ALLOWABLE
          FEE</h6></div>
      </div>
      <div v-if="!hasBundle" class="row estimate-line py-2" v-for="(line, index) in estimate.lines" :key="index">
        <span>
        <div class="col-auto line-action-col" v-if="!estimate.finalized  && estimate.isArchived === false">
          <button  v-if="!estimate.finalized  && estimate.isArchived === false"  @click.prevent="removeLine(index)" class="btn bg-transparent remove-line py-0">
            <font-awesome-icon :icon="['far', 'times-circle']"/>
          </button>
        </div>
        </span>
        <span v-if="estimate.finalized || estimate.isArchived === true">
          <div class="col-auto line-action-empty-col"></div>
          </span>
        <div class="col-4">{{ line.code + ' ' + line.description }}</div>
        <div class="col-2">{{ line.modifier }}</div>
        <div class="col-2 text-center">{{ line.discountEligible ? formatPercentage(line.discountRate) : 'N/A' }}</div>
        <div class="col-1 text-center">
         <span>
          <b-form-input v-if="!estimate.finalized && estimate.isArchived === false"
                        id="qtyField" v-model="line.quantity"
                        type="number" min="0"
                        @change="lineQuantityChanged()">
          </b-form-input>
           </span>
          <span v-if="estimate.finalized || estimate.isArchived === true">
          <div> {{line.quantity}}</div>
          </span>
        </div>
          <div class="col-2 text-right">{{ formatNumber(line.price) }}</div>
      </div>

      <div v-if="!hasBundle && !estimate.finalized && estimate.isArchived === false" class="row add-code py-2">
        <div class="col-4">
          <autocomplete :options="matchedCodes" v-model="searchTerm" @select="selectCode" placeholder="Add code...">
            <template slot-scope="{ option }">
              <div class="content">
                <p>{{ option.code }}<span v-if="option.modifier">&nbsp; {{ option.modifier }}</span><br>
                  {{ option.description }}</p>
              </div>
            </template>
          </autocomplete>
        </div>
      </div>
      <div v-if="!hasBundle && feeOptions" class="row py-2">
        <div class="col">
          <h5>Additional Professional Fees</h5>
        </div>
      </div>
      <div v-if="!hasBundle && feeOptions" class="row estimate-header py-2">
        <div class="col-auto line-action-header-col"></div>
        <div class="col-9"><h6>DESCRIPTION</h6></div>
        <div class="col-2 text-right"><h6>AMOUNT</h6></div>
      </div>
      <div v-if="!hasBundle && feeOptions" class="row estimate-line py-2" v-for="(fee, index) in estimate.fees" :key="'fee-' + index">
        <span>
        <div class="col-auto line-action-col" v-if="!estimate.finalized && estimate.isArchived === false">
          <button v-if="!estimate.finalized && estimate.isArchived === false" @click.prevent="removeFee(index)" class="btn bg-transparent remove-line py-0">
            <font-awesome-icon :icon="['far', 'times-circle']"/>
          </button>
        </div>
          </span>
        <span v-if="estimate.finalized || estimate.isArchived === true">
          <div class="col-auto line-action-empty-col"></div>
          </span>
        <div class="col-9">{{ fee.description }}
          <font-awesome-icon title="Item not covered" v-if="estimate.isSelfPay === false && !fee.coveredItem"
                             class="ml-2 orange" icon="exclamation-circle"/>
        </div>
        <div class="col-2 text-right">{{ formatNumber(fee.amount) }}</div>
      </div>
      <div v-if="!hasBundle && estimate.isSelfPay === false" class="row estimate-line py-2">
        <div class="col">
          <font-awesome-icon title="Item not covered" class="ml-2 orange" icon="exclamation-circle"/>
          Indicates amount does not count towards deductible.
        </div>
      </div>
      <div v-if="!hasBundle && feeOptions" class="row add-code py-2">
        <div v-if="!estimate.finalized && estimate.isArchived === false" class="col-4">
          <b-select class="input-border" v-model="selectedFeeOption" :options="feeOptions"
                    @change="addFee"></b-select>
        </div>
      </div>
      <span v-if="estimate.hasImplant && !hasBundle">
      <div class="row py-2">
        <div class="col">
          <h5>Implant</h5>
        </div>
      </div>
      <div class="row estimate-header py-2">
        <div class="col-auto line-action-header-col"></div>
        <div class="col-9"><h6>DESCRIPTION</h6></div>
        <div class="col-2 text-right"><h6>AMOUNT</h6></div>
      </div>
      <div class="row estimate-line py-2">
        <div class="col-auto line-action-header-col"></div>
        <div class="col-9">
        <span>
          <b-textarea v-if="!estimate.finalized && estimate.isArchived === false"
                      v-model="estimate.implantDescription"
                      @input="estimateChanged = true">
          </b-textarea>
          </span>
        <span v-if="estimate.finalized || estimate.isArchived === true">
          <div> {{estimate.implantDescription}}</div>
          </span>
      </div>
        <div class="col-2 text-right">
          <span>
          <b-form-input v-if="!estimate.finalized && estimate.isArchived === false"
                        class="text-right" v-model="estimate.implantCost"
                        :formatter="formatNumberString" lazy-formatter
                        @input="estimateChanged = true"></b-form-input>
            </span>
          <span v-if="estimate.finalized || estimate.isArchived === true">
          <div> {{formatNumberString(estimate.implantCost)}}</div>
          </span>
        </div>
      </div>
      </span>
      <span v-if="hasBundle && estimate.hasImplant">
      <div class="row py-2">
        <div class="col">
          <h5>Implant</h5>
        </div>
      </div>
      <div class="row estimate-header py-2">
        <div class="col-auto line-action-header-col"></div>
        <div class="col-3"><h6>CODE</h6></div>
        <div class="col-6"><h6>DESCRIPTION</h6></div>
        <div class="col-2 text-right"><h6>AMOUNT</h6></div>
      </div>
      <div class="row estimate-line py-2">
        <div class="col-auto line-action-header-col"></div>
        <div class="col-2">
          <span v-if="estimate.isArchived === true">
            {{estimate.implantCptCode}}
          </span>
          <span>
            <b-form-input v-if="estimate.isArchived === false"
                          v-model="estimate.implantCptCode"
                          @input="estimateChanged = true">
          </b-form-input>
            </span>
        </div>
        <div class="col-1"> </div>
        <div class="col-6">
        <span>
          <b-textarea v-if="estimate.isArchived === false"
                      v-model="estimate.implantDescription"
                      @input="estimateChanged = true">
          </b-textarea>
          </span>
        <span v-if="estimate.isArchived === true">
          <div> {{estimate.implantDescription}}</div>
          </span>
      </div>
        <div class="col-2 text-right">
          <span>
          <b-form-input v-if="!estimate.finalized && estimate.isArchived === false"
                        class="text-right" v-model="estimate.implantCost"
                        :formatter="formatNumberString" lazy-formatter
                        @input="estimateChanged = true"></b-form-input>
            </span>
          <span v-if="estimate.finalized || estimate.isArchived === true">
          <div> {{formatNumberString(estimate.implantCost)}}</div>
          </span>
        </div>
      </div>
      </span>
      <div v-if="!hasBundle && estimate.isAsc" class="row py-2">
        <div class="col">
          <h5>Facility Fees</h5>
        </div>
      </div>
      <div v-if="!hasBundle && estimate.isAsc" class="row estimate-header py-2">
        <div class="col-auto line-action-header-col"></div>
        <div class="col-4"><h6>CODE</h6></div>
        <div class="col-3"><h6>MODIFIER</h6></div>
        <div class="col-2 text-center"><h6>ALLOWABLE RATE</h6></div>
        <div class="col-1 text-center"><h6>QUANTITY</h6></div>
        <div class="col-1 text-right"><h6>AMOUNT</h6></div>
      </div>
      <div v-if="!hasBundle && estimate.isAsc" class="row estimate-line py-2" v-for="(fee, index) in estimate.ascLines"
           :key="'asc-line-' + index">
        <div class="col-auto line-action-empty-col">&nbsp;</div>
        <div class="col-4">{{ fee.code + ' ' + (fee.description == null ? '' : fee.description) }}</div>
        <div class="col-3">{{ fee.modifier }}</div>
        <div class="col-2 text-center">{{ fee.discountEligible ? formatPercentage(fee.discountRate) : 'N/A' }}</div>
        <div class="col-1 text-center">{{ fee.quantity }}</div>
        <div class="col-1 text-right">{{ formatNumber(fee.amount) }}</div>
      </div>
      <div v-if="!hasBundle && estimate.ascCopayAmount" class="row estimate-line py-2">
        <div class="col-auto line-action-empty-col">&nbsp;</div>
        <div class="col-9">Copay</div>
        <div class="col-2 text-right">{{ formatNumber(estimate.ascCopayAmount) }}</div>
      </div>
      <div v-if="!hasBundle && estimate.isAsc" class="row add-code py-2">
        <div class="col-4">
        </div>
      </div>
      <div v-if="!hasBundle && estimate.isAsc && ascFeeOptions" class="row py-2">
        <div class="col">
          <h5>Additional Facility Fees</h5>
        </div>
      </div>
      <div v-if="!hasBundle && estimate.isAsc && ascFeeOptions" class="row estimate-header py-2">
        <div class="col-auto line-action-header-col"></div>
        <div class="col-9"><h6>DESCRIPTION</h6></div>
        <div class="col-2 text-right"><h6>AMOUNT</h6></div>
      </div>
      <div v-if="!hasBundle && estimate.isAsc && ascFeeOptions" class="row estimate-line py-2" v-for="(ascFee, index) in estimate.ascFees" :key="'asc-fee-' + index">
        <span>
        <div class="col-auto line-action-col" v-if="!estimate.finalized && estimate.isArchived === false">
          <button @click.prevent="removeAscFee(index)" class="btn bg-transparent remove-line py-0">
            <font-awesome-icon :icon="['far', 'times-circle']"/>
          </button>
        </div>
          </span>
        <span v-if="estimate.finalized || estimate.isArchived === true">
          <div class="col-auto line-action-empty-col"></div>
          </span>
        <div class="col-9">{{ ascFee.description }}
          <font-awesome-icon title="Item not covered" v-if="estimate.isSelfPay === false && !ascFee.coveredItem"
                             class="ml-2 orange" icon="exclamation-circle"/>
        </div>
        <div class="col-2 text-right">{{ formatNumber(ascFee.amount) }}</div>
      </div>
      <div v-if="!hasBundle && estimate.isAsc && ascFeeOptions && estimate.isSelfPay === false" class="row estimate-line py-2">
        <div class="col">
          <font-awesome-icon title="Item not covered" class="ml-2 orange" icon="exclamation-circle"/>
          Indicates amount does not count towards deductible.
        </div>
      </div>
      <div v-if="!hasBundle && estimate.isAsc && ascFeeOptions && !estimate.finalized && estimate.isArchived === false" class="row add-code py-2">
        <div class="col-4">
          <b-select class="input-border" v-model="selectedAscFeeOption" :options="ascFeeOptions" @change="addAscFee"></b-select>
        </div>
      </div>
      <div class="row py-2 add-code total-row">
        <div class="col-8"/>
        <div class="col-4 text-right pr-5">
          <span class="total-header" v-if="!hasBundle">Estimate Total:</span>&nbsp;
          <span class="total-header" v-if="hasBundle">Episode Total:</span>&nbsp;
          <span class="total-value" v-if="hasBundle">$ {{ formatNumber(bundleTotal)}}</span></div>
      </div>

      <div v-if="!hasBundle">
      <div class="row py-2 gray">
        <div class="col-2">
          <h5>Co-Provider Fees</h5>
        </div>
        <div class="col-8">
          ⚠️ Expected fees to be billed by other provider(s). Not included in this Estimate Total.
        </div>
      </div>
      <div class="row estimate-header py-2 gray">
        <div class="col-auto line-action-header-col"></div>
        <div class="col-2"><h6>NAME</h6></div>
        <div class="col-3"><h6>DESCRIPTION</h6></div>
        <div class="col-2"><h6>NPI</h6></div>
        <div class="col-2"><h6>TIN</h6></div>
        <div class="col-2 text-right"><h6>AMOUNT</h6></div>
      </div>
      <div class="row estimate-line py-2 gray" v-for="(coProviderFee, index) in estimate.coProviderFees" :key="'cop-fee-' + index">
        <span>
        <div class="col-auto line-action-col" v-if="!estimate.finalized && estimate.isArchived === false">
          <button v-if="!estimate.finalized && estimate.isArchived === false" @click.prevent="removeCopFee(index)" class="btn bg-transparent remove-line py-0">
            <font-awesome-icon :icon="['far', 'times-circle']"/>
          </button>
        </div>
          </span>
        <span v-if="estimate.finalized || estimate.isArchived === true">
          <div class="col-auto line-action-empty-col"></div>
          </span>
        <div class="col-2">{{ formatProviderName(coProviderFee.coProvider) }}</div>
        <div class="col-3">{{ coProviderFee.description }}</div>
        <div class="col-2"> {{ coProviderFee.coProvider.npi }}</div>
        <div class="col-2"> {{ coProviderFee.coProvider.tin }}</div>
        <div class="col-2 text-right">
          <span>
          <b-form-input v-if="!hasBundle && !estimate.finalized && estimate.isArchived === false"
                        :disabled="estimate.finalized || estimate.isArchived === true"
                        class="text-right" v-model="coProviderFee.amount"
                        :formatter="formatNumberString" lazy-formatter
                        @input="estimateChanged = true"></b-form-input>
                </span>
          <span v-if="estimate.finalized || estimate.isArchived === true">
          <div> {{formatNumberString(coProviderFee.amount)}}</div>
          </span>
        </div>
      </div>
      <div class="row add-code py-2 gray">
        <div class="col-4" v-if="!estimate.finalized && estimate.isArchived === false">
          <b-select class="input-border" v-model="selectedCopFeeOption" :options="copFeeOptions" @change="addCopFee"></b-select>
        </div>
      </div>
      </div>
      <div class="row">
        <div class="col-6">
          <div>
              <b-card
                  style="max-width: 30rem"
              class="mt-4">
                <template #header>
                  <h5 class="mb-0">Patient Note</h5>
                </template>
                <b-card-text>
                  <div v-if="!estimate.estimateNote" class="row py-2">
                    <div class="col">
                      <h6>Add a note for the patient on the estimate</h6>
                    </div>
                  </div>
                  <div v-if="estimate.estimateNote" class="row">
                    <div class="col">
                      <div>{{ estimate.estimateNote }}</div>
                    </div>
                  </div>
                </b-card-text>
                <button type="button" :disabled="estimateChanged == true || estimate.finalized || estimate.isArchived === true" class="btn btn-outline-orange mr-2 float-right"
                        @click.prevent="showingNotes = true">{{ noteText }}
                </button>
              </b-card>
            </div>
          <div>
            <div v-if="orgSurveySets.length > 0">
            <b-card
                style="max-width: 30rem"
                class="mt-4">
              <template #header>
                <h5 class="mb-0">Patient Surveys</h5>
              </template>
              <b-card-text>
                <div class="row">
                <div v-if="estimateSurveySets.length > 0" class="col"> <h6>ASSIGNED SURVEY SET</h6>
                  <a @click.prevent.stop="selectEstimateSurvey()" href="#">{{estimateSurveySetName}} <font-awesome-icon size="sm" :icon="['far', 'edit']"/></a>
                </div>
                  <div class="col"><button type="button" class="mr-2 btn btn-outline-orange float-right" @click.prevent="showSurveySend()">
                Send New Survey
              </button></div>
                </div>
                <div v-if="estimate.surveyResults">
                <h6 class="dropdown-toggle mt-3" @click.prevent="toggleSurveys">SURVEY RESPONSES</h6>
                <transition name="slide">
                  <div v-show="showingSurveys">
                    <div v-for="result in estimate.surveyResults" :key="result.organizationPatientSurveyResultId">
                      <a @click.prevent.prevent="showSurveyResults(result)" href="#">{{ result.name }} </a>&nbsp;
                      <span class="field-label">DATE: </span>{{ formatDateShort(result.createdAt) }}&nbsp;
                      <div v-if="result.scoringType === 'Mapped'"><span class="field-label">SCORE: </span>{{ result.score2 }} </div>
                      <div v-else><span class="field-label">SCORE: </span>{{ result.score }} </div>
                    </div>
                  </div>
                </transition>
                </div>
              </b-card-text>
            </b-card>
            </div>
          </div>
        </div>
        <div class="col-6 text-right pr-4">
          <div v-if="!hasBundle" class="text-right mt-2 mb-4">
            <button :disabled="(!isOrgAdmin && !isAdmin && estimate.finalized) || estimate.isArchived === true" type="button" class="my-2 btn btn-orange float-right" @click.prevent="finalizeEstimate">{{ estimate.finalized ? 'Reopen Estimate' : 'Finalize Estimate'}}</button>
              <button v-if="!hasBundle || (hasBundle && !estimate.bundle.hasPaymentDistributions)" type="button" :disabled="estimateChanged == false || estimate.finalized || estimate.isArchived === true" class="my-2 btn btn-orange mr-2"
                      @click.prevent="updateEstimate()">
                <font-awesome-icon size="lg" class="mr-2" v-show="isSaving" icon="spinner" pulse/>
                Update Estimate
              </button>
            </div>
          <div v-if="hasBundle" class="text-right mt-2 mb-4">
          <button type="button" :disabled="estimateChanged == false && estimate.isArchived === true" class="my-2 btn btn-orange" @click.prevent="updateEstimate()">
            <font-awesome-icon size="lg" class="mr-2" v-show="isSaving" icon="spinner" pulse/>
            Update Episode
          </button>
            </div>
          <template v-if="!hasBundle && estimate.isSelfPay === false">
            <span class="total-header">Covered Items Total:</span>&nbsp;<span
              class="total-value">$ {{ formatNumber(estimate.coveredItemsTotal) }}</span><br/>
          </template>
          <template v-if="estimate.isSelfPay === false">
            <span v-if="!hasBundle">
              <span class="total-header">Estimate Total:</span>
              <span class="total-value">$ {{ formatNumber(estimate.estimateTotal) }}</span><br/>
            </span>
            <span v-if="hasBundle">
              <span class="total-header">Episode Total: &nbsp;</span><span class="total-value">$ {{formatNumber(this.bundleTotal)}}</span><br/>
            </span>
          </template>

          <span class="total-header final-total">Patient Total:</span>&nbsp;<span
            class="total-value final-total">$ {{ formatNumber(estimate.patientTotal) }}</span><br/>

          <template v-if="estimate.discountAmount > 0">
            <span class="total-header final-total">Discount Available:</span>&nbsp;<span
              class="total-value">$ {{ formatNumber(estimate.discountAmount) }}</span><br/>
            <span class="total-header final-total">Patient Total With Discount:</span>&nbsp;<span
              class="total-value final-total">$ {{ formatNumber(estimate.patientTotalWithDiscount) }}</span><br/>
            <span>Expires {{ formatDateUtc(estimate.discountExpiration)}}</span>&nbsp;<br/>
          </template>

          <span class="total-header final-total">Minimum Deposit:</span>&nbsp;<span class="total-value final-total">
                        <b-form-input v-if="!estimate.useDefaultDepositAmount" v-model="estimate.depositAmount"
                                      :formatter="formatNumberString" lazy-formatter
                                      @input="estimateChanged = true"></b-form-input>
                        <span v-if="estimate.useDefaultDepositAmount">${{
                            formatNumberString(estimate.depositAmount)
                          }}</span>
                    </span><br/>
          <span class="total-override final-total">
                        <b-form-checkbox v-model="estimate.useDefaultDepositAmount" :disabled="estimate.finalized || estimate.isArchived === true">
                            Use default deposit amount
                        </b-form-checkbox>
                    </span><br/>

          <div v-show="estimate.amountPaid">
            <span class="total-header final-total">Amount Paid:</span>&nbsp;<span
              class="total-value final-total">$ {{ formatNumber(estimate.amountPaid) }}</span><br/>
          </div>
          <div class="button-grouping">
            <div>
              <div class="button-header text-left ml-2">Payment</div>
              <div>
          <button v-if="!hasBundle || (hasBundle && !estimate.bundle.hasPaymentDistributions)" type="button" :disabled="estimateChanged == true" class="m-2 btn btn-orange wide-button"
                  @click.prevent="showingPayment = true">Payment
          </button>
              </div>
              <div>
          <button v-if="!hasBundle || (hasBundle && !estimate.bundle.hasPaymentDistributions)" :disabled="estimate.finalized || estimate.isArchived === true" type="button" class="m-2 btn btn-orange wide-button"
                  @click.prevent="showingEditDiscountModal = true">{{ estimate.discountAmount > 0 ? 'Edit Discount' : 'Add Discount' }}
          </button>
              </div>
              <div>
                <div>
                  <button v-if="hasBundle && estimate.patientTotal > 0" type="button" :disabled="estimateChanged == true" class="m-2 btn btn-orange wide-button"
                          @click.prevent="showingPayment = true">Payment
                  </button>
                </div>
          <button v-if="hasBundle && estimate.bundle.hasPaymentDistributions && !estimate.paymentDistribution" type="button" :disabled="estimateChanged == true || estimate.isArchived === true" class="m-2 btn btn-orange wide-button"
                  @click.prevent="startPaymentDistribution()">
            <font-awesome-icon size="lg" class="mr-2" v-show="isOpeningPaymentDistribution" icon="spinner" pulse/>
            {{ this.estimate.bundle.approvalRequired === true ? 'Request Approval' : 'Initiate Distribution'}}
          </button>
              </div>
              <div>
          <button v-if="hasBundle && estimate.bundle.hasPaymentDistributions && estimate.paymentDistribution" type="button" :disabled="estimateChanged == true || estimate.isArchived === true" class="m-2 btn btn-orange wide-button"
                  @click.prevent="openPaymentDistribution()">
            <font-awesome-icon size="lg" class="mr-2" v-show="isOpeningPaymentDistribution" icon="spinner" pulse/>
            Payment Distribution
          </button>
              </div>
            </div>

          <div>
            <div class="button-header text-left ml-2">Download</div>
            <div v-if="!hasBundle">
              <button type="button" :disabled="estimateChanged == true" @click.prevent="showEstimatePdf"
                      class="m-2 btn btn-outline-orange wide-button">Download Estimate
              </button>
            </div>
            <div v-if="hasBundle">
              <button type="button" :disabled="estimateChanged == true" @click.prevent="showEstimatePdf"
                      class="m-2 btn btn-outline-orange wide-button">Download Episode
              </button>
            </div>
            <div>
          <button type="button" :disabled="estimate.amountPaid <= 0" @click.prevent="showReceipt"
                  class="m-2 btn btn-outline-orange wide-button">Download Receipt
          </button>
            </div>
          </div>
          <div>
            <div class="button-header text-left ml-2">Send to Patient</div>
            <div v-if="!hasBundle">
          <button type="button" :disabled="estimateChanged == true" class="m-2 btn btn-outline-orange wide-button"
                  @click.prevent="showingSendModal = true">Send Estimate
          </button>
            </div>
            <div v-if="hasBundle">
              <button type="button" :disabled="estimateChanged == true" class="m-2 btn btn-outline-orange wide-button"
                      @click.prevent="showingSendModal = true">Send Episode
              </button>
            </div>
            <div>
              <button type="button" :disabled="estimate.amountPaid <= 0" @click.prevent="sendReceipt"
                      class="m-2 btn btn-outline-orange wide-button">Send Receipt
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
    <div v-if="!hasBundle" class="row total-row pt-4 mx-2">
      <div class="col-12">
        <div class="detail-block">
          <div class="detail-header">
            <h5 class="mb-2" v-if="!hasBundle">Organization:</h5>
          <h5 class="mb-2" v-if="hasBundle">Anchor Provider Group:</h5>
          {{ estimate.organizationName }}<br>
          Tax ID: {{ estimate.taxId }}<br>
          <template v-if="estimate.isAsc">
            Surgery Center: {{ estimate.ascOrganizationName }}<br>
            Tax ID: {{ estimate.ascTaxId }}<br>
          </template>
          <br>
          <template v-if="!hasBundle && estimate.isSelfPay === false">
            Insurance:
            {{
              estimate.insurance ? estimate.insurance.insuranceCompany + ' - ' + estimate.insurance.insurancePlan : 'None'
            }}<br>
          </template>
          </div>
          <div class="detail-header">
          <h5 class="mb-2">Location:</h5>
          <span v-if="!estimate.finalized && estimate.isArchived === false" >
              <a @click.prevent.stop="showingEstimateLocation = true" href="#">{{ estimate.location.name }}&nbsp;&nbsp;<font-awesome-icon
                  size="sm" :icon="['far', 'edit']"/>
            </a><br>
            </span>
          <span v-if="estimate.finalized || estimate.isArchived === true">
              <div>{{ estimate.location.name }}</div><br>
            </span>
          Type: {{ estimate.location.type }}<br>
          {{ estimate.location.street1 }}<br>
          <template v-if="estimate.location.street2">
            {{ estimate.location.street2 }}<br>
          </template>
          {{ estimate.location.city }}, {{ estimate.location.stateShort }} {{ estimate.location.zip }}<br><br>
          </div>
          <div class="detail-header">
          <h5 class="mb-2"><template v-if="hasBundle">Anchor </template>Provider:</h5>
          {{ estimate.providerName }}<br>
          <template v-if="estimate.provider">
            NPI: {{ estimate.provider.npi }}<br>
          </template>
          <br>
          </div>
          <div v-if="!hasBundle" class="detail-header">
            <h5 class="mb-2">Fee Schedules:</h5>
            <div v-if="!estimate.finalized && estimate.isArchived === false">
              Allowable Fee: <a @click.prevent.stop="chooseFeeSchedule(false)" :disabled="estimate.finalized || estimate.isArchived === true" href="#">{{ estimate.organizationAllowableFeeScheduleName }}&nbsp;&nbsp;<font-awesome-icon size="sm" :icon="['far', 'edit']"/><br></a></div>
            <div v-if="estimate.finalized || estimate.isArchived === true">
                Allowable Fee: {{estimate.organizationAllowableFeeScheduleName }}<br>
              </div>
            <template v-if="estimate.isAsc">
                <span v-if="!estimate.finalized && estimate.isArchived === false">
                  Facility Allowable Fee: <a @click.prevent.stop="chooseFeeSchedule(true)" href="#">{{ estimate.organizationAscFeeScheduleName }}&nbsp;&nbsp;<font-awesome-icon size="sm" :icon="['far', 'edit']"/></a>
                  <br>
                </span>
              <span v-if="estimate.finalized || estimate.isArchived === true">
                  Facility Allowable Fee: {{ estimate.organizationAscFeeScheduleName }}
                  Facility Allowable Fee: {{ estimate.organizationAscFeeScheduleName }}
                </span>
            </template>
            Medicare: {{ estimate.medicareFeeScheduleName }}<br><br>
          </div>
          <div v-if="!hasBundle && estimate.isAsc" class="detail-header">
            <h5 class="mb-2">Payment breakdown:</h5>
            Clinic Total: ${{ formatNumber(clinicAmount) }} <br>
            ASC Total: ${{ formatNumber(estimate.ascPatientTotal) }} <br><br>
          </div>
          <div class="detail-header">
            <h5 class="mb-2">Activity:</h5>
            <div v-if="estimate.createdByUser">Created By: {{ createdUser }}<br></div>
            <div v-if="estimate.lastUpdatedByUser">Last Updated By: {{ lastUpdated }}<br></div>
            <div v-if="!estimate.lastUpdatedByUser">Last Updated: {{ lastUpdated }}<br></div>
            Email Last Sent: {{ emailLastSent }}<br>
            Sms Last Sent: {{ smsLastSent }}<br>
          <br>
          </div>
          </div>
      </div>
      <div class="col-2">
      </div>
    </div>
    <div v-if="hasPayments" class="row py-2">
      <div class="col mt-4">
        <h5 class="ml-2">Payments</h5>
      </div>
    </div>
    <div v-if="hasPayments" class="row estimate-header py-2">
      <div class="col-auto line-action-header-col"></div>
      <div class="col-2"><h6>DATE</h6></div>
      <div class="col-3"><h6>COMPANY</h6></div>
      <div class="col-2"><h6>TYPE</h6></div>
      <div class="col-2"><h6>ACTION</h6></div>
      <div class="col-2 text-right"><h6>AMOUNT</h6></div>
    </div>
    <div v-if="hasPayments" class="row estimate-line py-2" v-for="(payment, index) in estimate.payments"
         :key="'payment-' + index">
      <div class="col-auto align-self-center line-action-empty-col"></div>
      <div class="col-2 align-self-center">{{ formatDate(payment.paymentDate) }}</div>
      <div class="col-3 align-self-center">{{ payment.organizationName }}</div>
      <div class="col-2 align-self-center">{{ paymentTypeDisplayName(payment) }}</div>
      <div class="col-2 align-self-center">
        <button v-if="isOrgAdmin && isRefundable(payment)" type="button" :disabled="estimateChanged == true || isSaving == true"
                class="mt-2 mb-2 btn btn-outline-orange btn-outline-gray" @click.prevent="refundDialog(payment)">Refund
        </button>
      </div>
      <div class="col-2 text-right align-self-center">$ {{ formatNumber(payment.amount) }}</div>
    </div>
    <div v-if="hasPayments" class="row add-code py-2 align-self-center">
      <div class="col-4">
      </div>
    </div>
    <confirm-modal @confirmed="handleConfirmed"
                   :title="modalTitle"
                   :copy="modalCopy"
                   :confirmButton="modalButton"
                   :visible="showConfirmModal"
                   :alertOnly= "alertOnly"
                   @update:visible="visible => showConfirmModal = visible">
      <div class="row mb-3">
      <div class="col-2"></div>
      <div class="col-8 mt-2 text-center">
        <div class="text-center">
          <font-awesome-icon size="sm" class="text-warning mr-2 align-baseline" icon="exclamation-triangle"/>
          {{modalCopy}}
        </div>
      </div>
      <div class="col-2"></div>
    </div>
    </confirm-modal>
    <refund-modal :payment="activePayment" :visible="showRefundConfirmation" @refundProcessed="refundProcessed"
                  @update:visible="visible => showRefundConfirmation = false"></refund-modal>
    <pdf-view-modal :title="pdfTitle" :url="pdfUrl" :visible="showingPdfModal"
                    @update:visible="visible => showingPdfModal = visible"></pdf-view-modal>
    <estimate-notes-modal :estimate="estimate" :visible="showingNotes" @noteProcessed="noteProcessed"
                          @update:visible="visible => showingNotes = visible"></estimate-notes-modal>
    <estimate-payment-modal :estimate="estimate" :visible="showingPayment" @paymentProcessed="paymentProcessed"
                            @update:visible="visible => showingPayment = visible"></estimate-payment-modal>
    <loading-modal :visible="showingLoadingModal"
                   @update:visible="visible => showingLoadingModal = visible"></loading-modal>
    <patient-estimate-fee-modal :visible="showingFeeModal" @addFee="addNewFee"
                        @update:visible="visible => showingFeeModal = visible"></patient-estimate-fee-modal>
    <patient-estimate-asc-fee-modal :visible="showingAscFeeModal" @addFee="addNewAscFee"
                        @update:visible="visible => showingAscFeeModal = visible"></patient-estimate-asc-fee-modal>
    <estimate-send-modal :estimate="estimate" :visible="showingSendModal" @estimateSent="estimateSent"
                         @update:visible="visible => showingSendModal = visible"></estimate-send-modal>
    <edit-patient-modal :id="editingPatientId" @patientUpdated="patientUpdated" :visible="showingPatientDetails"
                        @update:visible="visible => showingPatientDetails = visible"></edit-patient-modal>
    <estimate-location :currentLocationId="estimate.location.locationId" @locationSelected="locationUpdated"
                       :visible="showingEstimateLocation"
                       @update:visible="visible => showingEstimateLocation = visible"></estimate-location>
    <edit-benefits-modal :estimate="estimate" :visible="showingEditBenefitsModal" @updateBenefits="updateBenefits"
                         @update:visible="visible => showingEditBenefitsModal = visible"></edit-benefits-modal>
    <estimate-fee-schedule-modal :current-fee-schedule-id="modalFeeScheduleId" :is-asc="estimate.isAsc" :asc-location-id="estimate.location.locationId" :asc-fee-schedule="choosingAscFeeSchedule" :visible="showingEstimateFeeSchedule" @update:visible="visible => showingEstimateFeeSchedule = visible" @feeScheduleUpdated="feeScheduleUpdated"></estimate-fee-schedule-modal>
    <edit-estimate-type-modal :isSelfPay="modalIsSelfPay" :visible="showingEditEstimateTypeModal" :insurances="patientInsurances" @estimateTypeUpdated="estimateTypeUpdated" @update:visible="visible => showingEditEstimateTypeModal = visible"></edit-estimate-type-modal>
    <edit-estimate-discount-modal :discountPercent="estimate.discountPercentage" :discountExpiration="estimate.discountExpiration" :visible="showingEditDiscountModal" @estimateDiscountUpdated="estimateDiscountUpdated" @update:visible="visible => showingEditDiscountModal = visible"></edit-estimate-discount-modal>
    <estimate-cop-fee-modal :visible="showingCopFeeModal" @addNewCopFee="addNewCopFee" @update:visible="visible => showingCopFeeModal = visible"></estimate-cop-fee-modal>
    <survey-results-modal :visible="showingSurveyDetails" :result="estimateSurveyResult" @update:visible="visible => showingSurveyDetails = visible"></survey-results-modal>
    <survey-send-modal :visible="showingSurveySendModal" :id="estimate.organizationPatientEstimateId" @surveySent="surveySent" @update:visible="visible => showingSurveySendModal = visible"></survey-send-modal>
    <estimate-internal-notes-modal :visible="showingInternalNotesModal" :id="estimate.organizationPatientEstimateId" @updated="notesUpdated" @update:visible="visible => showingInternalNotesModal = visible"></estimate-internal-notes-modal>
    <finalize-estimate-modal :visible="showingFinalizeEstimateModal" :estimate="estimate" @updated="estimateUpdated" @update:visible="visible => showingFinalizeEstimateModal = visible"></finalize-estimate-modal>
    <select-survey-modal :visible="showingSelectSurveyModal" :estimate-id="estimate.organizationPatientEstimateId" :estimate-survey-set="estimate.estimateSurveySet" @updated="surveySelected" @update:visible="visible => showingSelectSurveyModal = visible"></select-survey-modal>
    <estimate-template-modal
                            :edit-estimate-template="editEstimateTemplate"
                             @updated="estimateTemplateUpdated"
                             :visible="showingEstimateTemplateModal"
                             @update:visible="visible => showingEstimateTemplateModal = visible"
    >
    </estimate-template-modal>
  </div>
</template>

<script>
  import api from '../services/api'
  import moment from 'moment-timezone'
  import LoadingModal from '../components/LoadingModal'
  import Autocomplete from '../components/Autocomplete'
  import EditPatientModal from '../components/EditPatientModal'
  import EditBenefitsModal from '../components/EditBenefitsModal'
  import EstimatePaymentModal from '../components/EstimatePaymentModal'
  import EstimateNotesModal from '../components/EstimateNotesModal'
  import EstimateSendModal from '../components/EstimateSendModal'
  import PdfViewModal from '../components/PdfViewModal'
  import _ from 'lodash'
  import VueScrollTo from 'vue-scrollto'
  import PatientEstimateFeeModal from '../components/PatientEstimateFeeModal'
  import PatientEstimateAscFeeModal from '../components/PatientEstimateAscFeeModal'
  import ErrorsMixin from '../mixins/ErrorsMixin'
  import EstimateLocation from '../components/EstimateLocation'
  import ConfirmModal from '../components/ConfirmModal'
  import RefundModal from '@/components/RefundModal'
  import EstimateFeeScheduleModal from '@/components/EstimateFeeScheduleModal'
  import EditEstimateTypeModal from '@/components/EditEstimateTypeModal'
  import EditEstimateDiscountModal from '@/components/EditEstimateDiscountModal'
  import EstimateCopFeeModal from '@/components/EstimateCopFeeModal'
  import SurveyResultsModal from '@/components/SurveyResultsModal'
  import SurveySendModal from '@/components/SurveySendModal'
  import EstimateInternalNotesModal from '@/components/EstimateInternalNotesModal'
  import FinalizeEstimateModal from '@/components/FinalizeEstimateModal'
  import SelectSurveyModal from '@/components/SelectSurveyModal'
  import AnalyticsMixin from '@/mixins/AnalyticsMixin'
  import EstimateTemplateModal from '@/components/EstimateTemplateModal.vue'

  export default {
    name: 'PatientEstimate',

    components: {
      EstimateTemplateModal,
      SelectSurveyModal,
      FinalizeEstimateModal,
      EstimateInternalNotesModal,
      LoadingModal,
      Autocomplete,
      EstimatePaymentModal,
      EstimateNotesModal,
      EstimateSendModal,
      PdfViewModal,
      PatientEstimateFeeModal,
      EditPatientModal,
      EstimateLocation,
      EditBenefitsModal,
      ConfirmModal,
      RefundModal,
      EstimateFeeScheduleModal,
      EditEstimateTypeModal,
      EditEstimateDiscountModal,
      EstimateCopFeeModal,
      SurveyResultsModal,
      SurveySendModal,
      PatientEstimateAscFeeModal
    },

    mixins: [ErrorsMixin, AnalyticsMixin],

    props: {
      id: {
        default: 0
      }
    },

    data () {
      return {
        benefitsEdit: false,
        modalFeeScheduleId: 0,
        choosingAscFeeSchedule: false,
        estimate: {
          depositAmount: 0.00,
          emailLastSentAt: null,
          isAsc: false,
          isSelfPay: false,
          benefitOverride: false,
          skipVerify: false,
          organizationAllowableFeeScheduleId: 0,
          organizationAscFeeScheduleId: 0,
          insurance: {
            verification: {
              insuranceEligibilityStatus: 'Verified',
              individualDeductibleMet: null,
              individualDeductible: null,
              individualMaxOopMet: null,
              individualMaxOop: null,
              familyDeductibleMet: null,
              familyDeductible: null,
              familyMaxOopMet: null,
              familyMaxOop: null
            }
          },
          location: {
            name: '',
            locationId: 0,
            ascOrganizationId: 0
          },
          deductibleAmount: null,
          coinsuranceAmount: null,
          maxOopAmount: null,
          lines: [],
          fees: [
            {
              amount: 0,
              coveredItem: false,
              description: '',
              organizationEstimateFeeId: 0,
              organizationPatientEstimateFeeId: 0
            }
          ],
          ascFees: [],
          useDefaultDepositAmount: true,
          ascCopayAmount: null,
          hasPaymentToken: false,
          last4: null,
          cardType: null,
          hasImplant: false,
          implantDescription: '',
          implantCost: 0.00,
          discountPercentage: 0.00,
          discountAmount: 0.00,
          discountExpiration: null,
          patientTotalWithDiscount: 0.00,
          primaryIcdCmCodeId: 0,
          secondaryIcdCmCodeId: 0,
          primaryIcdCmCode: {
            icdCmCodeId: 0,
            code: '',
            description: ''
          },
          secondaryIcdCmCode: {
            icdCmCodeId: 0,
            code: '',
            description: ''
          },
          coProviders: [],
          coProviderFees: [],
          surveyResults: [],
          createdByUser: {},
          lastUpdatedByUser: {}
        },
        patient: {
          organizationPatientId: 0
        },
        showingLoadingModal: false,
        showingEditBenefitsModal: false,
        showingPatientDetails: false,
        showingEstimateLocation: false,
        showingEstimateFeeSchedule: false,
        showingSendModal: false,
        editingPatientId: 0,
        searchTerm: '',
        matchedCodes: [],
        icdSearchTerm: '',
        primaryIcd: '',
        secondaryIcd: '',
        matchedIcdCodes: [],
        isSaving: false,
        isOpeningPaymentDistribution: false,
        estimateChanged: false,
        showingPayment: false,
        showingNotes: false,
        alertCountDown: 0,
        alertVariant: 'success',
        alertMessage: '',
        showingPdfModal: false,
        pdfUrl: '',
        pdfTitle: '',
        selectedFeeOption: 0,
        selectedAscFeeOption: 0,
        showingFeeModal: false,
        showingAscFeeModal: false,
        showRefundConfirmation: false,
        confirmRefundTitle: 'Refund payment?',
        confirmRefundCopy: 'Are you sure you want to refund this payment?',
        confirmRefundButton: 'Yes',
        cancelRefundButton: 'No',
        activePayment: {},
        showingEditEstimateTypeModal: false,
        modalIsSelfPay: false,
        showingEditDiscountModal: false,
        paymentTypeDisplayNames: {
          PartialRefund: 'Partial Refund'
        },
        showingCopFeeModal: false,
        selectedCopFeeOption: 0,
        patientInsurances: [],
        showingSurveySendModal: false,
        showingInternalNotesModal: false,
        showingFinalizeEstimateModal: false,
        estimateNotes: [],
        surveySets: [],
        showingSelectSurveyModal: false,
        estimateSurveySetName: 'Click to select ',
        estimateSurveySets: [],
        orgSurveySets: [],
        estimateSurveyResult: {},
        showingSurveyDetails: false,
        showingSurveys: false,
        editingEstimateTemplate: {
          ascFees: [],
          ascLines: [],
          coProviders: [],
          fees: [],
          hasImplant: false,
          implantCost: null,
          implantDescription: null,
          leadCode: null,
          leadCodeModifier: null,
          lines: [],
          name: '',
          organizationEstimateSurveySet: null,
          organizationEstimateSurveySetId: null,
          organizationId: 0,
          primaryIcdCmCode: {},
          primaryIcdCmCodeId: 0,
          secondaryIcdCmCode: {},
          secondaryIcdCmCodeId: 0
        },
        showingEstimateTemplateModal: false,
        editEstimateTemplate: {},
        showConfirmModal: false,
        currentConfirmation: '',
        archivableEstimate: {
          organizationPatientEstimateId: 0
        },
        modalTitle: '',
        modalCopy: '',
        modalButton: '',
        alertOnly: false,
        bundleTotal: ''
      }
    },

    watch: {
      // call again the method if the route changes
      $route: 'loadEstimate',
      searchTerm: _.debounce(function (searchTerm) {
        this.searchCodes()
      }, 500),

      primaryIcd: _.debounce(function (primaryIcd) {
        this.icdSearchTerm = primaryIcd
        this.searchIcdCodes()
      }, 500),

      secondaryIcd: _.debounce(function (secondaryIcd) {
        this.icdSearchTerm = secondaryIcd
        this.searchIcdCodes()
      }, 500),

      'estimate.useDefaultDepositAmount' (newValue, oldValue) {
        if (newValue === true) {
          this.estimate.depositAmount = this.estimate.defaultEstimateDepositPercentage * this.estimate.patientTotal
          this.estimateChanged = true
        }
      },

      'estimate.notes' (newValue, oldValue) {
        this.estimateNotes = _.clone(newValue)
      },

      'estimate.benefitOverride' (newValue, oldValue) {
        if (this.showingLoadingModal) {
          return
        }
        if (newValue !== oldValue) {
          if (newValue) {
            this.showingEditBenefitsModal = true
          }
          this.estimateChanged = true
        }
      }
    },

    computed: {
      dateOfServiceCopy () {
        if (this.estimate.procedureDate) {
          return (this.formatDateUtc(this.estimate.procedureDate))

        }
        return 'mm/dd/yyyy'
      },

      refundReminderTitle () {
        return 'Update ' + this.pmType
      },

      refundReminderCopy () {
        const paymentdate = moment(this.activePayment.paymentDate).format('MM/DD/YYYY')

        return 'We’ve refunded the payments, but the batch on ' + paymentdate + ' may need to be adjusted in ' + this.pmType + '.'
      },

      feeOptions () {
        if (!this.estimate.feeOptions) {
          return []
        }

        const options = [{
          text: 'Add a fee',
          value: 0
        }]
        this.estimate.feeOptions.forEach(option => {
          options.push({
            text: option.description,
            value: option.organizationEstimateFeeId
          })
        })

        options.push({
          text: 'Custom Fee',
          value: -1
        })

        return options
      },

      ascFeeOptions () {
        if (!this.estimate.ascFeeOptions) {
          return []
        }

        const options = [{
          text: 'Add a fee',
          value: 0
        }]
        this.estimate.ascFeeOptions.forEach(option => {
          options.push({
            text: option.description,
            value: option.organizationEstimateAscFeeId
          })
        })

        options.push({
          text: 'Custom Fee',
          value: -1
        })

        return options
      },

      copFeeOptions () {
        const options = [{
          text: 'Add Co-Provider',
          value: 0
        }]
        this.estimate.coProviders.forEach(option => {
          options.push({
            text: this.formatProviderName(option),
            value: option.organizationCoProviderId
          })
        })

        options.push({
          text: 'Custom Co-Provider Fee',
          value: -1
        })

        return options
      },

      isOrgAdmin () {
        return this.$store.isOrgAdmin
      },

      isAdmin () {
        return this.$store.isAdmin
      },

      pmType () {
        return this.$store.organization.pmType
      },

      noteText () {
        if (this.estimate.estimateNote) {
          return 'Edit'
        } else {
          return 'Add Patient Note'
        }
      },

      hasIndividualDeductible () {
        if (!this.estimate.insuranceVerification) {
          return false
        }

        if (!this.estimate.insuranceVerification.individualDeductibleMet && !this.estimate.insuranceVerification.individualDeductible) {
          return false
        }

        return true
      },

      individualDeductibleDisplay () {
        if (this.hasIndividualDeductible) {
          return '$' + this.formatNumber(this.estimate.insuranceVerification.individualDeductibleMet) + ' of $' + this.formatNumber(this.estimate.insuranceVerification.individualDeductible)
        }

        return 'Not Applicable'
      },

      hasIndividualMaxOop () {
        if (!this.estimate.insuranceVerification) {
          return false
        }

        if (!this.estimate.insuranceVerification.individualMaxOopMet && !this.estimate.insuranceVerification.individualMaxOop) {
          return false
        }

        return true
      },

      individualMaxOopDisplay () {
        if (this.hasIndividualMaxOop) {
          return '$' + this.formatNumber(this.estimate.insuranceVerification.individualMaxOopMet) + ' of $' + this.formatNumber(this.estimate.insuranceVerification.individualMaxOop)
        }

        return 'Not Applicable'
      },

      hasFamilyDeductible () {
        if (!this.estimate.insuranceVerification) {
          return false
        }

        if (!this.estimate.insuranceVerification.familyDeductibleMet && !this.estimate.insuranceVerification.familyDeductible) {
          return false
        }

        return true
      },

      familyDeductibleDisplay () {
        if (this.hasFamilyDeductible) {
          return '$' + this.formatNumber(this.estimate.insuranceVerification.familyDeductibleMet) + ' of $' + this.formatNumber(this.estimate.insuranceVerification.familyDeductible)
        }

        return 'Not Applicable'
      },

      hasFamilyMaxOop () {
        if (!this.estimate.insuranceVerification) {
          return false
        }

        if (!this.estimate.insuranceVerification.familyMaxOopMet && !this.estimate.insuranceVerification.familyMaxOop) {
          return false
        }

        return true
      },

      familyMaxOopDisplay () {
        if (this.hasFamilyMaxOop) {
          return '$' + this.formatNumber(this.estimate.insuranceVerification.familyMaxOopMet) + ' of $' + this.formatNumber(this.estimate.insuranceVerification.familyMaxOop)
        }

        return 'Not Applicable'
      },

      individualDeductiblePercent () {
        if (!this.estimate.insuranceVerification) {
          return
        }
        return (this.estimate.insuranceVerification.individualDeductibleMet / this.estimate.insuranceVerification.individualDeductible) * 100
      },

      individualOutOfPocketPercent () {
        if (!this.estimate.insuranceVerification) {
          return
        }
        return (this.estimate.insuranceVerification.individualMaxOopMet / this.estimate.insuranceVerification.individualMaxOop) * 100
      },

      hasFamilyBenefits () {
        if (!this.estimate.insuranceVerification) {
          return false
        }

        if (!this.estimate.insuranceVerification.familyDeductibleMet &&
          !this.estimate.insuranceVerification.familyDeductible &&
          !this.estimate.insuranceVerification.familyMaxOopMet &&
          !this.estimate.insuranceVerification.familyMaxOop) {
          return false
        }

        return true
      },

      familyDeductiblePercent () {
        if (!this.estimate.insuranceVerification) {
          return
        }
        return (this.estimate.insuranceVerification.familyDeductibleMet / this.estimate.insuranceVerification.familyDeductible) * 100
      },

      familyOutOfPocketPercent () {
        if (!this.estimate.insuranceVerification) {
          return
        }
        return (this.estimate.insuranceVerification.familyMaxOopMet / this.estimate.insuranceVerification.familyMaxOop) * 100
      },

      coinsuranceTotal () {
        if (this.estimate.coinsurance) {
          return (this.estimate.coinsurance * 100).toFixed(0) + '%'
        } else {
          return 'None'
        }
      },

      ascCoinsuranceTotal () {
        if (this.estimate.ascCoinsurance) {
          return (this.estimate.ascCoinsurance * 100).toFixed(0) + '%'
        } else {
          return 'None'
        }
      },

      emailLastSent () {
        if (this.estimate.emailLastSentAt) {
          return moment(this.estimate.emailLastSentAt).format('MM/DD/YY h:mm a')
        } else {
          return 'Not sent'
        }
      },

      smsLastSent () {
        if (this.estimate.smsLastSentAt) {
          return moment(this.estimate.smsLastSentAt).format('MM/DD/YY h:mm A')
        } else {
          return 'Not sent'
        }
      },

      lastUpdated () {
        if (this.estimate.updatedAt && !this.estimate.lastUpdatedByUser) {
          return moment(this.estimate.updatedAt).format('MM/DD/YY h:mm A')
        } else if (this.estimate.updatedAt && this.estimate.lastUpdatedByUser) {
          return this.estimate.lastUpdatedByUser.firstName + ' ' + this.estimate.lastUpdatedByUser.lastName + ' ' + moment(this.estimate.updatedAt).format('MM/DD/YY [at] h:mm A')
        } else {
          return ''
        }
      },

      createdUser () {
        if (this.estimate.createdByUser) {
          return this.estimate.createdByUser.firstName + ' ' + this.estimate.createdByUser.lastName + ' ' + moment(this.estimate.createdAt).format('MM/DD/YY [at] h:mm A')
        } else {
          return ''
        }
      },

      hasPayments () {
        if (this.estimate.payments && this.estimate.payments.length > 0) {
          return true
        }

        return false
      },

      clinicAmount () {
        const amt = this.estimate.patientTotal - this.estimate.ascPatientTotal
        return amt
      },

      hasBundle () {
        return !!this.estimate.organizationBundleId
      },

      estimateType () {
        if (this.estimate.isSelfPay) {
          return 'Self Pay'
        } else if (!this.hasBundle && this.estimate.isSelfPay === false && this.estimate.insurance && this.estimate.insurance.insurancePlan) {
          return this.estimate.insurance.insurancePlan
        } else if (this.hasBundle && this.estimate.employer) {
          return this.estimate.employer.name
        }

        return 'Unknown'
      }
    },

    methods: {
      formatDate (date) {
        if (date) {
          return moment(date).format('MM/DD/YY h:mm A')
        } else {
          return ''
        }
      },

      toggleSurveys () {
        this.showingSurveys = !this.showingSurveys
      },

      formatDateShort (date) {
        if (date) {
          return moment(date).format('MM/DD/YY')
        } else {
          return ''
        }
      },

      formatDateUtc (date) {
        if (date) {
          return moment(date).utc().format('MM/DD/YYYY')
        }

        return ''
      },

      formatNumber (number) {
        if (number) {
          return number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
        }

        return '0.00'
      },

      updateBundleTotal () {
        if (this.estimate.hasImplant) {
          this.bundleTotal = parseFloat(this.estimate.bundle?.amount) + parseFloat(this.estimate.implantCost)
        } else {
          this.bundleTotal = this.estimate.bundle?.amount
        }
      },

      formatProviderName (coProvider) {
        let name = coProvider.facilityName
        if (coProvider.lastName) {
          name += ' - ' + coProvider.lastName
          if (coProvider.firstName) {
            name += ', ' + coProvider.firstName
          }
        }

        return name
      },

      formatPercentage (number) {
        if (number) {
          return (number * 100).toFixed(0) + '%'
        }

        return '0%'
      },

      numberFromString (value) {
        if (value) {
          value = value.replace(',', '')
          return Number.parseFloat(value)
        }

        return 0
      },

      formatNumberString (value) {
        let number = 0.00
        if (typeof value === 'string') {
          value = value.replace(',', '')
          number = this.numberFromString(value)
        } else {
          number = value
        }

        if (number) {
          return this.formatNumber(number)
        }

        return number
      },

      formatICD10 (code) {
        if (code.length > 6) {
          return code.substr(0, 3) + '.' + code.substr(3, 3) + '.' + code.substr(6)
        } else if (code.length > 3) {
          return code.substr(0, 3) + '.' + code.substr(3)
        }
        return code
      },

      formatTerm (days) {
        if (days === 0) {
          return 'DOS '
        } else {
          return days + 'D '
        }
      },

      transformEstimateToTemplate () {
        this.editEstimateTemplate.lines = this.estimate.lines
        this.editEstimateTemplate.ascFees = this.estimate.ascFees
        this.editEstimateTemplate.fees = this.estimate.fees
        this.editEstimateTemplate.coProviders = this.estimate.coProviderFees
        this.editEstimateTemplate.primaryIcdCmCode = this.estimate.primaryIcdCmCode
        this.editEstimateTemplate.primaryIcdCmCodeId = this.estimate.primaryIcdCmCodeId
        this.editEstimateTemplate.secondaryIcdCmCode = this.estimate.secondaryIcdCmCode
        this.editEstimateTemplate.secondaryIcdCmCodeId = this.estimate.secondaryIcdCmCodeId
        this.editEstimateTemplate.hasImplant = this.estimate.hasImplant
        this.editEstimateTemplate.implantCost = this.estimate.implantCost
        this.editEstimateTemplate.implantDescription = this.estimate.implantDescription
        this.editEstimateTemplate.organizationEstimateSurveySet = this.estimate.estimateSurveySet
        if (this.estimate.estimateSurveySet) {
          this.editEstimateTemplate.organizationEstimateSurveySetId = this.estimate.estimateSurveySet.organizationEstimateSurveySetId
        }
      },

      populateEditEstimateTemplate () {
        this.transformEstimateToTemplate()
        this.editEstimateTemplate = {
          lines: this.estimate.lines,
          ascFees: this.estimate.ascFees,
          fees: this.estimate.fees,
          coProviders: this.estimate.coProviderFees,
          primaryIcdCmCode: this.estimate.primaryIcdCmCode,
          primaryIcdCmCodeId: this.estimate.primaryIcdCmCodeId,
          secondaryIcdCmCode: this.estimate.secondaryIcdCmCode,
          secondaryIcdCmCodeId: this.estimate.secondaryIcdCmCodeId,
          hasImplant: this.estimate.hasImplant,
          implantCost: this.estimate.implantCost,
          implantDescription: this.estimate.implantDescription,
          organizationEstimateSurveySet: this.estimate.estimateSurveySet,
          organizationEstimateSurveySetId: this.estimate.estimateSurveySet
            ? this.estimate.estimateSurveySet.organizationEstimateSurveySetId
            : null
        }
      },

      createEstimateTemplate () {
        this.populateEditEstimateTemplate()
        this.editingEstimateTemplate = this.editEstimateTemplate
        this.showingEstimateTemplateModal = true
      },

      estimateTemplateUpdated () {
        this.showingEstimateTemplateModal = false
      },

      editPatient (patientId) {
        this.editingPatientId = patientId
        this.showingPatientDetails = true
      },

      patientUpdated (updatedPatient) {
        this.showingPatientDetails = false
        this.patient = updatedPatient
        this.estimate.patient = updatedPatient
        if (!this.patient.imageUrl) {
          this.patient.imageUrl = 'https://s3.amazonaws.com/health-here-assets/Images/PhotoPlaceholder.jpg'
        }
      },

      locationUpdated (locationId) {
        this.showingEstimateLocation = false
        if (locationId !== this.estimate.locationId) {
          this.estimate.location = {
            locationId
          }

          this.updateEstimate()
          this.estimateChanged = true
        }
      },

      chooseFeeSchedule (ascFeeSchedule) {
        if (ascFeeSchedule) {
          this.choosingAscFeeSchedule = true
          this.modalFeeScheduleId = this.estimate.organizationAscFeeScheduleId
          this.showingEstimateFeeSchedule = true
        } else {
          this.choosingAscFeeSchedule = false
          this.modalFeeScheduleId = this.estimate.organizationAllowableFeeScheduleId
          this.showingEstimateFeeSchedule = true
        }
      },

      feeScheduleUpdated (newFeeScheduleId) {
        if (this.choosingAscFeeSchedule) {
          this.estimate.organizationAscFeeScheduleId = newFeeScheduleId
        } else {
          this.estimate.organizationAllowableFeeScheduleId = newFeeScheduleId
        }

        this.showingEstimateFeeSchedule = false
        this.estimateChanged = true
        this.updateEstimate()
      },

      isRefundable (payment) {
        if (payment.refundableAmount > 0) {
          return true
        }

        return false
      },

      loadEstimate () {
        const insurances = []
        this.showingLoadingModal = true
        this.clearAllErrors()

        api.get('/Dashboard/Estimate/' + this.id).then(response => {
          this.trackEvent(this.$options.name, 'Load', 'ResponseTime', response.config.requestDuration)
          this.estimateChanged = false
          this.estimate = response.data
          this.patient = this.estimate.patient
          if (this.estimate.estimateSurveySet !== null) {
            this.estimateSurveySetName = this.estimate.estimateSurveySet.name
          }

          if (this.estimate.patient.primaryInsurance) {
            insurances.push(this.estimate.patient.primaryInsurance)
          }

          if (this.estimate.patient.secondaryInsurance) {
            insurances.push(this.estimate.patient.secondaryInsurance)
          }
          this.patientInsurances = insurances

          if (!this.estimate.lines) {
            this.estimate.lines = []
          }
          // Sort the lines by discount rate
          this.estimate.lines.sort((a, b) => {
            return a.discountEligible ? b.discountRate - a.discountRate : -1
          })
          if (!this.estimate.fees) {
            this.estimate.fees = []
          }
          if (!this.estimate.ascFees) {
            this.estimate.ascFees = []
          }
          if (this.estimate.hasAsc && !this.estimate.ascLines) {
            this.estimate.ascLines = []
          }
          if (this.estimate.hasAsc) {
            // Sort the lines by discount rate
            this.estimate.lines.sort((a, b) => {
              return b.discountRate - a.discountRate
            })
          }
          if (!this.patient.imageUrl) {
            this.patient.imageUrl = 'https://s3.amazonaws.com/health-here-assets/Images/PhotoPlaceholder.jpg'
          }
          this.estimate.depositAmount = this.formatNumber(this.estimate.depositAmount)
          if (this.estimate.implantCost) {
            this.estimate.implantCost = this.formatNumber(this.estimate.implantCost)
          }
          if (this.estimate.primaryIcdCmCode) {
            this.primaryIcd = this.estimate.primaryIcdCmCode.code + ' - ' + this.estimate.primaryIcdCmCode.description
          }
          if (this.estimate.secondaryIcdCmCode) {
            this.secondaryIcd = this.estimate.secondaryIcdCmCode.code + ' - ' + this.estimate.secondaryIcdCmCode.description
          }
          if (this.estimate.bundle) {
            this.updateBundleTotal()
          }
          this.loadSurveySets()
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.$log.error('Error attempting to load estimate: ' + error.message, error)
            this.$store.logErrorResponse(this.$options.name, 'LoadError', error)
            if (error.response && error.response.status === 404) {
              this.addErrorForField('exception', 'Could not find this estimate.')
            } else {
              if (error.response && error.response.data) {
                this.setErrorsFromResponse(error.response.data)
              } else {
                this.addErrorForField('exception', error.message)
              }
            }
          }
        }).then(() => {
          // always executed
          this.showingLoadingModal = false
        })
      },
      handleConfirmed (confirmation) {
        if (this.currentConfirmation === 'reminder') {
          this.reminderConfirmed(confirmation)
        } else if (this.currentConfirmation === 'archive') {
          this.archiveConfirmed(confirmation)
        }
      },
      confirmEstimateArchive (estimate) {
        this.currentConfirmation = 'archive'
        this.archivableEstimate = estimate
        if (this.hasBundle) {
          this.modalTitle = 'Archive Episode'
          this.modalCopy = 'Are you sure you want to archive this episode?'
        } else {
          this.modalTitle = 'Archive Estimate'
          this.modalCopy = 'Are you sure you want to archive this estimate?'
        }
        this.modalButton = 'Archive'
        this.alertOnly = false
        this.showConfirmModal = true
      },
      archiveConfirmed (confirmation) {
        this.showConfirmModal = false
        if (confirmation === true && this.archivableEstimate.organizationPatientEstimateId > 0) {
          this.archiveEstimate(this.archivableEstimate.organizationPatientEstimateId)
        }
        this.archivableEstimate = {
          organizationPatientEstimateId: 0
        }
      },
      archiveEstimate (organizationPatientEstimateId) {
        const archiveValue = this.estimate.isArchived ? 'False' : 'True'
        api.post('/Dashboard/Estimate/Archive/' + organizationPatientEstimateId + '/' + archiveValue).then(response => {
          this.estimate.isArchived = !this.estimate.isArchived
          this.trackEvent('Estimate', 'Archive', response.config.requestDuration)
          this.loadEstimate()
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.trackEvent('Estimate', 'EstimateError', error.message)
            this.$log.error('Error setting estimate status: ' + error.message, error)
            this.errors = ['Error updating estimate status: ' + error.message]
          }
        }).then(() => {
          // Workaround for https://github.com/bootstrap-vue/bootstrap-vue/issues/1826
          setTimeout(() => {
            this.showingLoadingModal = false
          }, 100)
        })
      },
      benefitsUpdateConfirmed (confirmed) {
        this.showBenefitsConfirmation = false

        if (confirmed) {
          this.estimate.skipVerify = true
        }

        this.updateEstimate()
      },

      updateEstimate () {
        this.clearAllErrors()
        this.isSaving = true
        if (!this.hasBundle) {
          this.$refs.popover.$emit('close')
        }

        if (typeof this.estimate.depositAmount !== 'number') {
          this.estimate.depositAmount = this.numberFromString(this.estimate.depositAmount)
        }

        if (!this.hasBundle && this.estimate.isSelfPay === false && this.patientInsurances.length === 1) {
          this.estimate.organizationPatientInsuranceId = this.patientInsurances[0].organizationPatientInsuranceId
        }

        api.post('/Dashboard/Estimate/' + this.id, this.estimate, { timeout: 180000 }).then(response => {
          this.trackEvent(this.$options.name, 'Save', 'ResponseTime', response.config.requestDuration)

          this.estimate = response.data
          this.updateBundleTotal()
          this.patient = this.estimate.patient
          if (!this.estimate.lines) {
            this.estimate.lines = []
          }
          if (!this.estimate.fees) {
            this.estimate.fees = []
          }
          if (!this.estimate.ascFees) {
            this.estimate.ascFees = []
          }
          if (!this.patient.imageUrl) {
            this.patient.imageUrl = 'https://s3.amazonaws.com/health-here-assets/Images/PhotoPlaceholder.jpg'
          }
          this.estimate.depositAmount = this.formatNumber(this.estimate.depositAmount)
          this.estimateChanged = false
          if (this.choosingAscFeeSchedule) {
            this.modalFeeScheduleId = this.estimate.organizationAscFeeScheduleId
          } else {
            this.modalFeeScheduleId = this.estimate.organizationAllowableFeeScheduleId
          }
          if (this.estimate.implantCost) {
            this.estimate.implantCost = this.formatNumber(this.estimate.implantCost)
          }
          if (this.estimate.primaryIcdCmCode) {
            this.primaryIcd = this.estimate.primaryIcdCmCode.code + ' - ' + this.estimate.primaryIcdCmCode.description
          }
          if (this.estimate.secondaryIcdCmCode) {
            this.secondaryIcd = this.estimate.secondaryIcdCmCode.code + ' - ' + this.estimate.secondaryIcdCmCode.description
          }
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.$scrollToTop()
            this.$store.logErrorResponse(this.$options.name, 'SaveError', error)
            if (error.response && error.response.data) {
              this.setErrorsFromResponse(error.response.data)
            } else {
              this.addErrorForField('exception', error.message)
            }
            this.$log.error('Error attempting to save estimate: ' + error.message, error)
          }
        }).then(() => {
          // always executed
          this.isSaving = false
        })
      },

      showEstimatePdf () {
        const title = 'Estimate for ' + this.patient.lastName + ', ' + this.patient.firstName
        this.showPdf('Estimate', title)
      },

      showReceipt () {
        const title = 'Receipt for ' + this.patient.lastName + ', ' + this.patient.firstName
        this.showPdf('PaymentTransactionReceipt', title)
      },

      showPdf (type, title) {
        this.showingLoadingModal = true

        this.pdfTitle = title
        api.get('/Dashboard/Estimate/' + this.id + '/Report/' + type + '/', {
          responseType: 'blob',
          headers: { 'Content-Type': 'application/pdf' }
        }).then(response => {
          if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(response.data, 'Receipt.pdf')
          } else {
            this.pdfUrl = URL.createObjectURL(response.data)
            this.showingPdfModal = true
          }
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.$log.error('Error attempting to load facesheet: ' + error.message, error)
            this.addErrorForField('exception', error.message)
          }
        }).then(() => {
          // always executed
          this.showingLoadingModal = false
        })
      },

      showSurveyResults (result) {
        this.estimateSurveyResult = result
        this.showingSurveyDetails = true
      },

      showSurveySend () {
        this.showingSurveySendModal = true
      },

      surveySent () {
        this.showingSurveySendModal = false
      },

      viewBenefits (organizationPatientInsuranceVerificationId) {
        this.$router.push({ name: 'insurance-benefits', params: { id: organizationPatientInsuranceVerificationId, mode: 'estimate' } })
      },
      reminderConfirmed () {
        this.showConfirmModal = false
      },
      refundProcessed () {
        this.showRefundConfirmation = false
        this.showConfirmModal = true
        this.currentConfirmation = 'reminder'
        this.modalTitle = this.refundReminderTitle
        this.modalCopy = this.refundReminderCopy
        this.modalButton = 'Ok'
        this.alertOnly = true
        this.showConfirmModal = true
        this.loadEstimate()
        this.isSaving = false
      },

      refundDialog (payment) {
        this.activePayment = payment
        this.showRefundConfirmation = true
      },

      refund () {
        const payment = this.activePayment
        this.isSaving = true

        api.post('/Dashboard/Refund/' + payment.organizationPatientPaymentId).then(response => {
          this.trackEvent('Visit', 'Void', 'ResponseTime', response.config.requestDuration)

          if (payment.visitId) {
            this.trackEvent('Visit', 'Void', 'Success', payment.visitId)
          }

          this.visitRefundPending = {}
          this.confirmingRefund = false
          this.showConfirmation = false
          this.showConfirmModal = true

          this.loadEstimate()
          this.isSaving = false
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.trackEvent('Visit', 'VoidError', error.message)
            this.$log.error('Error attempting to void payment.', error)
          }
        })
      },

      closePopover () {
        this.$refs.popover.$emit('close')
      },

      sendEmail () {
        this.clearAllErrors()

        api.post('/Dashboard/Estimate/' + this.id + '/Email/Payment').then(response => {
          this.alertMessage = 'Email sent'
          this.alertCountDown = 20
          VueScrollTo.scrollTo(this.$refs.alertContainer, 0.2)
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.$log.error('Error attempting to send email: ' + error.message, error)
            if (error.response && error.response.data) {
              this.setErrorsFromResponse(error.response.data)
            } else {
              this.addErrorForField('exception', error.message)
            }
          }
        }).then(() => {
          // always executed
          this.showingLoadingModal = false
        })
      },

      sendSms () {
        this.clearAllErrors()

        api.post('/Dashboard/Estimate/' + this.id + '/Sms/Payment').then(response => {
          this.alertMessage = 'Sms/Text sent'
          this.alertCountDown = 20
          VueScrollTo.scrollTo(this.$refs.alertContainer, 0.2)
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.$log.error('Error attempting to send sms: ' + error.message, error)
            if (error.response && error.response.data) {
              this.setErrorsFromResponse(error.response.data)
            } else {
              this.addErrorForField('exception', error.message)
            }
          }
        }).then(() => {
          // always executed
          this.showingLoadingModal = false
        })
      },

      updateBenefits () {
        this.benefitsEdit = false
        this.loadEstimate()
      },

      estimateSent () {
        this.showingSendModal = false
      },

      noteProcessed () {
        this.showingNotes = false
        this.loadEstimate()
      },

      paymentProcessed (payments) {
        this.showingPayment = false
        this.loadEstimate()
        let totalAmount = 0.0
        for (const payment of payments) {
          totalAmount += payment.amount
        }
        this.alertMessage = 'Added payment for $' + totalAmount.toFixed(2)
        this.alertCountDown = 15
        VueScrollTo.scrollTo(this.$refs.alertContainer, 0.2)
      },

      searchCodes () {
        if (!this.searchTerm) {
          this.matchedCodes = []
          return
        }

        const parameters = {
          searchTerm: this.searchTerm,
          medicareFeeScheduleId: this.estimate.medicareFeeScheduleId,
          organizationAllowableFeeScheduleId: this.estimate.organizationAllowableFeeScheduleId
        }

        api.post('/Dashboard/CodeSearch', parameters).then(response => {

          this.matchedCodes = response.data
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.$log.error('Error attempting to search for code: ' + error.message, error)
          }
        })
      },

      searchIcdCodes () {
        if (!this.icdSearchTerm) {
          this.matchedIcdCodes = []
          return
        }

        const parameters = {
          searchTerm: this.icdSearchTerm.replace(/[\s.]+/g, '')
        }

        api.post('/Dashboard/IcdCodeSearch', parameters).then(response => {

          this.matchedIcdCodes = response.data
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.$log.error('Error attempting to search for ICD10 code: ' + error.message, error)
          }
        })
      },

      selectCode (code) {
        this.searchTerm = ''
        this.estimate.lines.push({
          code: code.code,
          description: code.description,
          modifier: code.modifier ? code.modifier : null,
          quantity: 1,
          price: 0.00
        })
        this.estimateChanged = true
      },

      selectPrimaryIcdCode (code) {
        this.icdSearchTerm = ''
        this.matchedIcdCodes = []
        // work around for value not updating properly in autocomplete component
        this.primaryIcd = ''
        this.$nextTick(() => {
          this.primaryIcd = this.formatICD10(code.code)
          this.estimate.primaryIcdCmCodeId = code.icdCmCodeId
          this.estimateChanged = true
        })
      },

      selectSecondaryIcdCode (code) {
        this.icdSearchTerm = ''
        this.matchedIcdCodes = []
        // work around for value not updating properly in autocomplete component
        this.secondaryIcd = ''
        this.$nextTick(() => {
          this.secondaryIcd = this.formatICD10(code.code)
          this.estimate.secondaryIcdCmCodeId = code.icdCmCodeId
          this.estimateChanged = true
        })
      },

      lineQuantityChanged () {
        this.estimateChanged = true
      },

      addFee (feeId) {
        if (feeId === -1) {
          this.selectedFeeOption = 0
          this.showingFeeModal = true
          return
        }

        const orgFee = this.estimate.feeOptions.filter(anOption => {
          return anOption.organizationEstimateFeeId === feeId
        }).shift()

        if (orgFee) {
          this.estimate.fees.push({
            organizationEstimateFeeId: feeId,
            description: orgFee.description,
            coveredItem: orgFee.coveredItem,
            amount: 0.00
          })

          this.estimateChanged = true
        }

        this.selectedFeeOption = 0
      },

      addAscFee (ascFeeId) {
        if (ascFeeId === -1) {
          this.selectedAscFeeOption = 0
          this.showingAscFeeModal = true
          return
        }

        const orgFee = this.estimate.ascFeeOptions.filter(anOption => {
          return anOption.organizationEstimateAscFeeId === ascFeeId
        }).shift()

        if (orgFee) {
          this.estimate.ascFees.push({
            organizationEstimateAscFeeId: ascFeeId,
            description: orgFee.description,
            coveredItem: orgFee.coveredItem,
            amount: 0.00
          })

          this.estimateChanged = true
        }

        this.selectedAscFeeOption = 0

      },

      addCopFee (copFeeId) {
        if (copFeeId === -1) {
          this.selectedCopFeeOption = 0
          this.showingCopFeeModal = true
          return
        }

        const copFee = this.estimate.coProviders.filter(anOption => {
          return anOption.organizationCoProviderId === copFeeId
        }).shift()

        if (copFee) {
          this.estimate.coProviderFees.push({
            organizationCoProviderId: copFeeId,
            coProvider: copFee,
            description: copFee.description,
            amount: copFee.amount
          })

          this.estimateChanged = true
        }

        this.selectedCopFeeOption = 0
      },

      addNewFee (fee) {
        this.showingFeeModal = false
        this.estimate.fees.push(fee)
        this.estimateChanged = true
      },

      addNewAscFee (fee) {
        this.showingAscFeeModal = false
        this.estimate.ascFees.push(fee)
        this.estimateChanged = true
      },

      addNewCopFee (fee) {
        this.showingCopFeeModal = false
        this.estimate.coProviderFees.push(fee)
        this.estimateChanged = true
      },

      removeFee (index) {
        this.estimateChanged = true
        this.estimate.fees.splice(index, 1)
      },

      removeAscFee (index) {
        this.estimateChanged = true
        this.estimate.ascFees.splice(index, 1)
      },

      removeLine (index) {
        this.estimateChanged = true
        this.estimate.lines.splice(index, 1)
      },

      removeCopFee (index) {
        this.estimateChanged = true
        this.estimate.coProviderFees.splice(index, 1)
      },

      formatBirthDate (dob) {
        if (dob) {
          return moment(dob).utc().format('MM/DD/YYYY')
        } else {
          return ''
        }
      },

      paymentTypeDisplayName (payment) {
        if (this.paymentTypeDisplayNames[payment.creditCardPaymentType]) {
          return this.paymentTypeDisplayNames[payment.creditCardPaymentType]
        }

        return payment.creditCardPaymentType
      },

      startPaymentDistribution () {
        if (this.isOpeningPaymentDistribution) {
          return
        }
        if (!this.estimate.primaryIcdCmCodeId && !this.estimate.secondaryIcdCmCodeId) {
          this.addErrorForField('exception', 'Please select an ICD-10 Code')
          this.$scrollToTop()
          return
        }
        if (this.estimate.hasImplant && this.estimate.bundle && this.estimate.bundle.implantRequired === true && !this.estimate.implantCptCode) {
          this.addErrorForField('exception', 'Please enter an implant code')
          this.$scrollToTop()
          return
        }
        this.isOpeningPaymentDistribution = true
        api.get('/Dashboard/Estimate/' + this.estimate.organizationPatientEstimateId + '/PaymentDistribution').then(response => {

          const paymentDistribution = response.data
          this.$router.push({ name: 'bundle-payment', params: { id: paymentDistribution.organizationBundlePaymentDistributionId } })
          this.isOpeningPaymentDistribution = false
        }).catch(error => {
          this.isOpeningPaymentDistribution = false
          if (!this.$store.canHandleNetworkError(error)) {
            this.$scrollToTop()
            this.$store.logErrorResponse(this.$options.name, 'SaveError', error)
            if (error.response && error.response.data) {
              this.setErrorsFromResponse(error.response.data)
            } else {
              this.addErrorForField('exception', error.message)
            }
            this.$log.error('Error attempting to start payment distribution estimate: ' + error.message, error)
          }
        })
      },

      openPaymentDistribution () {
        if (this.estimate.finalized === false && !this.estimate.primaryIcdCmCodeId && !this.estimate.secondaryIcdCmCodeId) {
          this.addErrorForField('exception', 'Please select an Icd-10 code')
          this.$scrollToTop()
          return
        }
        this.$router.push({ name: 'bundle-payment', params: { id: this.estimate.paymentDistribution.organizationBundlePaymentDistributionId } })
      },

      editEstimateType () {
        this.modalIsSelfPay = this.estimate.isSelfPay
        this.showingEditEstimateTypeModal = true
      },

      estimateTypeUpdated (isSelfPay, insurance) {
        this.showingEditEstimateTypeModal = false
        if (this.estimate.isSelfPay !== isSelfPay) {
          this.estimate.isSelfPay = isSelfPay
          this.estimate.organizationAscFeeScheduleId = null // reset it and let the api figure out the correct schedule
          this.estimate.organizationAllowableFeeScheduleId = null
        }
        if (this.estimate.organizationPatientInsuranceId !== insurance.organizationPatientInsuranceId) {
          this.estimate.organizationPatientInsuranceId = insurance.organizationPatientInsuranceId
        }
        this.updateEstimate()
      },

      estimateDiscountUpdated (discountPercentage, discountExpiration) {
        this.showingEditDiscountModal = false
        this.estimate.discountPercentage = discountPercentage
        this.estimate.discountExpiration = discountExpiration
        this.updateEstimate()
      },

      sendReceipt () {
        this.isSaving = true
        api.post('Dashboard/Receipt/' + this.estimate.payments[0].organizationPatientPaymentId).then(response => {
          this.$emit('receiptSent', response)
          this.isSaving = false
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting to send receipt: ' + error.message, error)
          }
          this.isSaving = false
        })
      },

      updateNotes () {
        this.showingInternalNotesModal = true
      },

      notesUpdated (notes) {
        this.estimateNotes = notes
      },

      finalizeEstimate () {
        if (this.estimate.finalized) {
          const value = !this.estimate.finalized

          this.clearAllErrors()
          api.post('/Dashboard/Estimate/' + this.estimate.organizationPatientEstimateId + '/Finalize/' + value).then(() => {
            this.loadEstimate()
          }).catch(error => {
            if (!this.$store.canHandleNetworkError(error)) {
              this.$log.error('Error attempting to update estimate: ' + error.message, error)
              this.addErrorForField('exception', error.message)
            }
          })
        } else {
          this.showingFinalizeEstimateModal = true
        }
      },

      estimateUpdated () {
        this.showingFinalizeEstimateModal = false
        this.loadEstimate()
      },

      selectEstimateSurvey () {
        this.showingSelectSurveyModal = true
      },

      surveySelected () {
        this.showingSelectSurveyModal = false
        this.loadEstimate()
      },

      loadSurveySets () {
        api.get('Dashboard/Estimate/' + this.estimate.organizationPatientEstimateId + '/EstimateSurveySets').then(response => {
          this.estimateSurveySets = response.data
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting to load code survey sets: ' + error.message, error)
          }
        })
        api.get('Dashboard/SurveySets').then(response => {
          this.orgSurveySets = response.data
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting to load code survey sets: ' + error.message, error)
          }
        })
      },

      updateProcedureDate () {
        this.estimate.quickSave = true
        this.updateEstimate()
      }
    },

    beforeRouteUpdate (to, from, next) {
      this.clearAllErrors()
      this.estimate = {
        insurance: {
          verification: {}
        },
        location: {
          name: ''
        },
        deductibleAmount: null,
        coinsuranceAmount: null,
        maxOopAmount: null,
        lines: [],
        fees: []
      }
      this.patient = {
        organizationPatientId: 0
      }
    },
    created () {
      this.loadEstimate()
    }
  }
</script>

<style scoped>
.patient-summary-header {
  border-top: 1px solid #FFA800;
  background-color: #f9f9f9;
}

.patient-summary-header h5 {
  color: #001859;
}

.patient-summary .column {
  padding: 0.75rem;
  vertical-align: bottom;
  border-collapse: collapse;
}

.patient-summary {
  border-bottom: 1px solid #dee2e6;
  background-color: #f9f9f9;
  /*cursor: pointer;*/
}

.estimate-header, .estimate-line {
  border-bottom: 1px solid #dee2e6;
}

.estimate-header h6 {
  color: #001859;
  margin-bottom: .5em;
  margin-top: .5em;
}

.estimate-line, .total-row {
  font-size: 1.25em;
}

.line-action-header-col {
  min-width: 72px;
}

.line-action-empty-col {
  min-width: 72px;
}

.btn.remove-line:HOVER {
  color: rgb(255, 70, 0);
}

.add-code, .bundle-container {
  border-bottom: 2px solid #176CC6;
}

.gray {
  background-color: #f9f9f9;
}

.detail-block {
  display: flex;
  justify-content: space-between;
  font-size: .75em;
  background-color: #f9f9f9;
  padding: 1.5em;
}

.episode-detail-block {
  display: flex;
  justify-content: space-between;
  font-size: .75em;
  background-color: #f9f9f9;
}

.detail-block h5 {
  font-size: 1.2em;
}

.total-header {
  color: #2A84F4;
  font-weight: bolder;
  font-size: 17.5px;
}

.total-override {
  font-weight: bolder;
}

.final-total {
  font-weight: 700;
}

.total-value {
  width: 150px;
  display: inline-block;
  font-size: 17.5px;
}

.total-value input[type=text] {
  text-align: right;
}

.procedure-date {
  max-width: 200px;
}

.input-border {
  border: 1px solid rgb(255, 70, 0);
  border-radius: 5px;
}

.input-border:focus {
  box-shadow: none;
}

.notes-outline {
  border-style: solid;
  border-width: 1px;
  width: 50%;
}

.benefits-btn {
  margin-left: 0px !important;
}

/* we need to still inherit the 'orange' style since it also
    contains the button stylings
*/
.btn-outline-gray {
  border-color: #d3d3d3 !important;
}

.bundle-container {
  font-size: 1.25em;
}

.employer-logo {
  max-height: 150px;
  max-width: 150px;
}

p.card-text h6 {
  color: #001859;
}

.card-body {
 background: rgb(0, 0, 0, 0.03);
}

.wide-button {
  width: 200px;
}

.button-header {
  font-weight: 500;
}

.button-grouping{
  display: flex;
  flex-direction: row-reverse;
}

.detail-header {
  max-width: 250px;
}

.detail-header h5{
  color: #001859;
}

.dropdown-toggle {
  cursor: pointer;
}
.dropdown-toggle:hover {
  text-decoration: underline;
}

.episode-header {
  display:flex;
  justify-content: space-between;
  align-items: center;
}

.episode-header h1{
  color: rgb(255, 70, 0);
  font-weight: lighter;
  font-size: 28px;
  margin: 0;
}

.episode-row {
  margin-top: 20px;
  border-top: 1px solid #dee2e6;
  width: 100%;
}

.dateOfServiceField {
  background-color: white;
  border: 1px solid #ced4da;
  color: #495057;
}

#popover-target-1.btn:focus {
    outline: none;
    background-color: white!important;
    color: #495057;
    border: 1px solid #ced4da;
    box-shadow: none;
  }

#popover-target-1.btn:hover {
  cursor: text;
}

.wide-popover {
  min-width: 295px; }

.icd-code-container {
  padding-right: 100px;
}
</style>
